import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LayoutDashboard, Film, Building2, Calendar, LogOut, Check, X, Bell, Lock, Unlock, Layers, Plus, Trash2, GripVertical, Edit2, Save, Image, Sparkles, Video, Upload, Loader2, ChevronDown, ChevronUp, Eye, EyeOff, FileText, MapPin, Phone, Mail, Monitor, Users, Hash, AlertCircle, Copy, DollarSign, Percent, Link2, ChevronLeft, ChevronRight, BookOpen, HelpCircle, Settings, Zap, Radio, Shield, Send, Star, BarChart3, TrendingUp, Globe, Smartphone, Tablet, MousePointer, Clock, Activity, Box, Mic, ExternalLink, Play, RotateCcw, Bot, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { contentDatabase } from '../data/mock';
import ContentEditor from '../components/ContentEditor';
import ScreeningsDashboard from '../components/ScreeningsDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";

// Available icons for hero features
const AVAILABLE_ICONS = [
  'Music', 'Subtitles', 'Video', 'User', 'Tv', 'Monitor', 
  'Headphones', 'Globe', 'Star', 'Camera', 'Film', 'Play', 'Mic', 'MessageCircle'
];

// Bookings Calendar Component
const BookingsCalendarView = ({ bookings, contents, cinemas }) => {
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  const approvedBookings = bookings.filter(b => b.status === 'approved');
  
  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };
  
  // Parse date from various formats (dd/mm/yyyy or yyyy-mm-dd)
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('/')) {
      const [d, m, y] = dateStr.split('/');
      return { day: parseInt(d), month: parseInt(m), year: parseInt(y) };
    } else if (dateStr.includes('-')) {
      const [y, m, d] = dateStr.split('-');
      return { day: parseInt(d), month: parseInt(m), year: parseInt(y) };
    }
    return null;
  };
  
  // Format date for display (dd/mm/yyyy)
  const formatDateDisplay = (dateStr) => {
    const parsed = parseDate(dateStr);
    if (!parsed) return dateStr;
    return `${String(parsed.day).padStart(2, '0')}/${String(parsed.month).padStart(2, '0')}/${parsed.year}`;
  };
  
  const getBookingsForDate = (dateStr) => {
    const targetParsed = parseDate(dateStr);
    if (!targetParsed) return [];
    
    return approvedBookings.filter(b => {
      return b.screenings?.some(s => {
        const screeningParsed = parseDate(s.date);
        if (!screeningParsed) return false;
        return screeningParsed.day === targetParsed.day && 
               screeningParsed.month === targetParsed.month && 
               screeningParsed.year === targetParsed.year;
      });
    });
  };
  
  // Get all bookings for the current month
  const getMonthBookings = () => {
    const monthBookings = [];
    approvedBookings.forEach(b => {
      b.screenings?.forEach(s => {
        const parsed = parseDate(s.date);
        if (parsed && parsed.month === month + 1 && parsed.year === year) {
          monthBookings.push({
            ...b,
            screeningDate: s.date,
            screeningTime: s.time,
            parsedDate: parsed
          });
        }
      });
    });
    // Sort by date
    return monthBookings.sort((a, b) => {
      return new Date(a.parsedDate.year, a.parsedDate.month-1, a.parsedDate.day) - 
             new Date(b.parsedDate.year, b.parsedDate.month-1, b.parsedDate.day);
    });
  };
  
  const formatDateStr = (year, month, day) => {
    const d = String(day).padStart(2, '0');
    const m = String(month + 1).padStart(2, '0');
    return `${d}/${m}/${year}`;
  };
  
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const monthBookings = getMonthBookings();
  
  // Get bookings for selected date
  const selectedDateBookings = selectedDate ? getBookingsForDate(selectedDate) : [];
  
  return (
    <div className="bg-[#141414] rounded-xl p-6">
      <div className="flex gap-6">
        {/* Left side - Calendar */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCalendarDate(new Date(year, month - 1, 1))}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <h2 className="text-white text-xl font-semibold">
                {monthNames[month]} {year}
              </h2>
              <button
                onClick={() => setCalendarDate(new Date(year, month + 1, 1))}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-white/60 text-sm">Con reservas</span>
              </div>
            </div>
          </div>
          
          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {dayNames.map(day => (
              <div key={day} className="text-center text-white/50 text-sm py-2 font-medium">
                {day}
              </div>
            ))}
            
            {/* Empty cells for days before first of month */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-16 bg-white/5 rounded-lg" />
            ))}
            
            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = formatDateStr(year, month, day);
              const dayBookings = getBookingsForDate(dateStr);
              const hasBookings = dayBookings.length > 0;
              const isToday = today.getDate() === day && 
                              today.getMonth() === month && 
                              today.getFullYear() === year;
              const isSelected = selectedDate === dateStr;
              
              return (
                <div
                  key={day}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`h-16 p-2 rounded-lg transition-all cursor-pointer relative ${
                    isSelected 
                      ? 'bg-[#e4002b] border-2 border-white' 
                      : isToday 
                        ? 'bg-[#e4002b]/20 border border-[#e4002b]' 
                        : hasBookings 
                          ? 'bg-green-500/20 border border-green-500/50 hover:bg-green-500/30' 
                          : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <span className={`text-sm font-medium ${
                    isSelected ? 'text-white' : isToday ? 'text-[#e4002b]' : 'text-white/70'
                  }`}>
                    {day}
                  </span>
                  {hasBookings && (
                    <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {dayBookings.slice(0, 4).map((_, idx) => (
                        <div key={idx} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-green-400'}`} />
                      ))}
                      {dayBookings.length > 4 && (
                        <span className={`text-[10px] ml-0.5 ${isSelected ? 'text-white' : 'text-green-400'}`}>+{dayBookings.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Selected day details */}
          {selectedDate && (
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-lg">
                  Reservas del {selectedDate}
                </h3>
                <button 
                  onClick={() => setSelectedDate(null)}
                  className="text-white/50 hover:text-white text-sm"
                >
                  Cerrar
                </button>
              </div>
              
              {selectedDateBookings.length === 0 ? (
                <p className="text-white/50 text-center py-6">No hay reservas para este día</p>
              ) : (
                <div className="space-y-3">
                  {selectedDateBookings.map((booking, idx) => {
                    const content = contents.find(c => c.id === booking.content_id);
                    const cinema = cinemas.find(c => c.id === booking.cinema_id);
                    const screening = booking.screenings?.find(s => s.date === selectedDate);
                    
                    return (
                      <div key={idx} className="flex gap-4 p-3 bg-white/5 rounded-lg">
                        {/* Content image */}
                        <div className="w-20 h-28 bg-white/10 rounded-lg overflow-hidden flex-shrink-0">
                          {content?.image ? (
                            <img src={content.image} alt={content.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Film className="w-8 h-8 text-white/30" />
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1">
                          <h4 className="text-white font-semibold">{content?.title || 'Contenido'}</h4>
                          <p className="text-[#e4002b] text-sm">{content?.composer || ''}</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-white/60 text-sm flex items-center gap-2">
                              <Building2 className="w-4 h-4" />
                              {cinema?.venue_name || 'Cine'}
                            </p>
                            <p className="text-white/60 text-sm flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {selectedDate} {screening?.time && `- ${screening.time}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Aprobada
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Right side - Month bookings list */}
        <div className="w-80 flex-shrink-0">
          <div className="bg-white/5 rounded-xl p-4 h-full">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#e4002b]" />
              Reservas de {monthNames[month]}
            </h3>
            
            {monthBookings.length === 0 ? (
              <p className="text-white/50 text-center py-8">No hay reservas este mes</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {monthBookings.map((booking, idx) => {
                  const content = contents.find(c => c.id === booking.content_id);
                  const cinema = cinemas.find(c => c.id === booking.cinema_id);
                  
                  return (
                    <div 
                      key={idx} 
                      className="flex gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                      onClick={() => setSelectedDate(formatDateStr(booking.parsedDate.year, booking.parsedDate.month - 1, booking.parsedDate.day))}
                    >
                      {/* Thumbnail */}
                      <div className="w-12 h-16 bg-white/10 rounded overflow-hidden flex-shrink-0">
                        {content?.image ? (
                          <img src={content.image} alt={content.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Film className="w-5 h-5 text-white/30" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm truncate">{content?.title || 'Contenido'}</p>
                        <p className="text-white/50 text-xs truncate">{cinema?.venue_name || 'Cine'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-green-400 text-xs font-medium">
                            {formatDateDisplay(booking.screeningDate)}
                          </span>
                          {booking.screeningTime && (
                            <span className="text-white/40 text-xs">
                              {booking.screeningTime}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Summary */}
            <div className="mt-4 pt-4 border-t border-white/10">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">{monthBookings.length}</p>
                  <p className="text-white/50 text-xs">Proyecciones</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-400">{new Set(monthBookings.map(b => b.cinema_id)).size}</p>
                  <p className="text-white/50 text-xs">Cines</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [cinemas, setCinemas] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [contentRestrictions, setContentRestrictions] = useState({});
  const [homeSections, setHomeSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [newSectionType, setNewSectionType] = useState('content_carousel');
  
  // Inquiries state (special conditions)
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [inquiryResponse, setInquiryResponse] = useState({
    admin_response: '',
    pricing_type: 'tanto_alzado',
    exhibitor_percentage: 50,
    flat_fee_price: 400
  });
  const [savingInquiry, setSavingInquiry] = useState(false);
  
  // Hero state
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroFeatures, setHeroFeatures] = useState([]);
  const [editingSlide, setEditingSlide] = useState(null);
  const [slideForm, setSlideForm] = useState({});
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeatureIcon, setNewFeatureIcon] = useState('Video');
  const [newFeatureLabel, setNewFeatureLabel] = useState('');

  // Content management state
  const [contents, setContents] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [showContentEditor, setShowContentEditor] = useState(false);
  const [selectedContentForEdit, setSelectedContentForEdit] = useState(null);
  const [contentMedia, setContentMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  
  // Section content management state
  const [expandedSection, setExpandedSection] = useState(null);
  const [sectionContents, setSectionContents] = useState({});
  const [contentSearchQuery, setContentSearchQuery] = useState('');
  const [showContentDropdown, setShowContentDropdown] = useState(null);
  
  // Cinema detail modal state
  const [selectedCinema, setSelectedCinema] = useState(null);
  const [showCinemaDetail, setShowCinemaDetail] = useState(false);
  const [openAccordionSections, setOpenAccordionSections] = useState(['bravo']); // Default open section
  
  // Booking change requests state
  const [changeRequests, setChangeRequests] = useState([]);
  const [pendingChangeRequestsCount, setPendingChangeRequestsCount] = useState(0);
  const [changeRequestModal, setChangeRequestModal] = useState(null); // {requestId, action: 'approve'|'reject'}
  const [changeRequestNotes, setChangeRequestNotes] = useState('');
  const [processingChangeRequest, setProcessingChangeRequest] = useState(false);
  const [changeRequestsTab, setChangeRequestsTab] = useState('pending'); // pending, approved, rejected, deleted
  
  // Inquiries tab state
  const [inquiriesTab, setInquiriesTab] = useState('pending'); // pending, responded, accepted, rejected, deleted
  
  // Cinema tab state
  const [cinemasTab, setCinemasTab] = useState('all'); // all, pending, approved, rejected, deleted
  
  // Bookings tab state  
  const [bookingsTab, setBookingsTab] = useState('all'); // all, pending, approved, rejected, completed, deleted
  
  // Delete confirmation modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // {type: 'cinema'|'booking'|'inquiry'|'change-request', id: string, name: string, permanent: boolean}
  const [deleting, setDeleting] = useState(false);
  
  // Bravo number modal state
  const [showBravoModal, setShowBravoModal] = useState(false);
  const [bravoNumber, setBravoNumber] = useState('');
  const [savingBravo, setSavingBravo] = useState(false);
  
  // Cinema pricing state
  const [cinemaPricings, setCinemaPricings] = useState({});
  const [selectedCinemaPricing, setSelectedCinemaPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    pricing_type: 'tanto_alzado',
    exhibitor_percentage: 50,
    directo_opera_ballet_concert: 450,
    encore_opera_ballet_concert: 400,
    grabado_opera_ballet_concert: 350,
    pelicula: 350,
    documental: 200,
    // Bravo rental mode
    bravo_rental_mode: 'por_evento',
    bravo_rental_payment: 'unico',
    bravo_rental_price: 50
  });
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingModalTab, setPricingModalTab] = useState('tarifa'); // 'tarifa' or 'bravo'

  // Podcasts state
  const [podcasts, setPodcasts] = useState([]);
  const [editingPodcast, setEditingPodcast] = useState(null);
  const [showPodcastForm, setShowPodcastForm] = useState(false);
  const [podcastForm, setPodcastForm] = useState({
    title: '',
    subtitle: '',
    image_url: '',
    spotify_url: '',
    is_enabled: true,
    is_locked: false,
    order: 0
  });
  const [savingPodcast, setSavingPodcast] = useState(false);

  // Booking filters state
  const [bookingFilters, setBookingFilters] = useState({
    month: '',
    cinema_id: '',
    content_id: ''
  });

  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [editingTestimonial, setEditingTestimonial] = useState(null);

  // Navigation menus state
  const [navMenus, setNavMenus] = useState([]);
  const [editingMenu, setEditingMenu] = useState(null);

  // Bravo cinema network state
  const [bravoCinemas, setBravoCinemas] = useState([]);
  const [bravoForm, setBravoForm] = useState({ nombre: '', circuito: '', poblacion: '', provincia: '', comunidad_autonoma: '' });
  const [bravoUploading, setBravoUploading] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    person_name: '',
    person_image: '',
    position: '',
    cinema_name: '',
    quote: '',
    order: 0,
    is_active: true
  });
  const [savingTestimonial, setSavingTestimonial] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // RAG Documents state
  const [ragDocuments, setRagDocuments] = useState([]);
  const [ragStats, setRagStats] = useState({ total_chunks: 0, documents_count: 0 });
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docUploadError, setDocUploadError] = useState('');

  // Turandot Inscripciones state
  const [turandotInscripciones, setTurandotInscripciones] = useState([]);

  useEffect(() => {
    const session = localStorage.getItem('admin_session');
    if (!session) { navigate('/admin/login'); return; }
    setAdmin(JSON.parse(session));
    loadData();
    loadContentRestrictions();
    loadSections();
    loadHeroData();
    loadContents();
    loadCinemaPricings();
    loadInquiries();
    loadTestimonials();
    loadPodcasts();
    loadNavMenus();
    loadBravoCinemas();
    loadChangeRequests();
    loadRagDocuments();
    loadTurandotInscripciones();
  }, [navigate]);

  const loadData = async () => {
    try {
      const [statsRes, cinemasRes, bookingsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/stats`),
        fetch(`${BACKEND_URL}/api/cinemas`),
        fetch(`${BACKEND_URL}/api/bookings`)
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (cinemasRes.ok) setCinemas(await cinemasRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
    } catch (err) { console.error('Error:', err); }
  };

  const loadHeroData = async () => {
    try {
      const [slidesRes, featuresRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/hero-slides`),
        fetch(`${BACKEND_URL}/api/hero-features`)
      ]);
      if (slidesRes.ok) setHeroSlides(await slidesRes.json());
      if (featuresRes.ok) setHeroFeatures(await featuresRes.json());
    } catch (err) { console.error('Error loading hero data:', err); }
  };

  const loadCinemaPricings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinema-pricing`);
      if (res.ok) {
        const data = await res.json();
        const pricingMap = {};
        data.forEach(p => { pricingMap[p.cinema_id] = p; });
        setCinemaPricings(pricingMap);
      }
    } catch (err) { console.error('Error loading cinema pricings:', err); }
  };

  const loadInquiries = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/inquiries`);
      if (res.ok) {
        const data = await res.json();
        setInquiries(data);
      }
    } catch (err) { console.error('Error loading inquiries:', err); }
  };

  const loadChangeRequests = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking-change-requests`);
      if (res.ok) {
        const data = await res.json();
        setChangeRequests(data);
        setPendingChangeRequestsCount(data.filter(r => r.status === 'pending').length);
      }
    } catch (err) { console.error('Error loading change requests:', err); }
  };

  const processChangeRequest = async (requestId, action, notes = '') => {
    setProcessingChangeRequest(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/booking-change-requests/${requestId}/process?action=${action}&admin_notes=${encodeURIComponent(notes)}`, {
        method: 'PUT'
      });
      if (res.ok) {
        loadChangeRequests();
        loadBookings();
        setChangeRequestModal(null);
        setChangeRequestNotes('');
      }
    } catch (err) { console.error('Error processing change request:', err); }
    setProcessingChangeRequest(false);
  };

  // Generic delete function for all entities
  const handleDelete = async (type, id, permanent = false) => {
    setDeleting(true);
    try {
      const endpoints = {
        'cinema': `/api/cinemas/${id}`,
        'booking': `/api/bookings/${id}`,
        'inquiry': `/api/inquiries/${id}`,
        'change-request': `/api/booking-change-requests/${id}`
      };
      
      const url = `${BACKEND_URL}${endpoints[type]}${permanent ? '?permanent=true' : ''}`;
      const res = await fetch(url, { method: 'DELETE' });
      
      if (res.ok) {
        // Reload the appropriate data
        if (type === 'cinema') loadCinemas();
        else if (type === 'booking') loadBookings();
        else if (type === 'inquiry') loadInquiries();
        else if (type === 'change-request') loadChangeRequests();
        
        setDeleteConfirmModal(null);
      }
    } catch (err) { console.error(`Error deleting ${type}:`, err); }
    setDeleting(false);
  };

  // Generic restore function for all entities
  const handleRestore = async (type, id) => {
    try {
      const endpoints = {
        'cinema': `/api/cinemas/${id}/restore`,
        'booking': `/api/bookings/${id}/restore`,
        'inquiry': `/api/inquiries/${id}/restore`,
        'change-request': `/api/booking-change-requests/${id}/restore`
      };
      
      const res = await fetch(`${BACKEND_URL}${endpoints[type]}`, { method: 'PUT' });
      
      if (res.ok) {
        if (type === 'cinema') loadCinemas();
        else if (type === 'booking') loadBookings();
        else if (type === 'inquiry') loadInquiries();
        else if (type === 'change-request') loadChangeRequests();
      }
    } catch (err) { console.error(`Error restoring ${type}:`, err); }
  };

  const loadTestimonials = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/testimonials`);
      if (res.ok) setTestimonials(await res.json());
    } catch (err) { console.error('Error loading testimonials:', err); }
  };

  const loadTurandotInscripciones = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/turandot-inscripciones`);
      if (res.ok) setTurandotInscripciones(await res.json());
    } catch (err) { console.error('Error loading Turandot inscripciones:', err); }
  };

  const updateTurandotInscripcionStatus = async (id, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/turandot-inscripciones/${id}?status=${status}`, {
        method: 'PUT'
      });
      if (res.ok) {
        loadTurandotInscripciones();
        toast.success('Estado actualizado');
      }
    } catch (err) { console.error('Error updating inscription:', err); }
  };

  const saveTestimonial = async () => {
    setSavingTestimonial(true);
    try {
      const method = editingTestimonial ? 'PUT' : 'POST';
      const url = editingTestimonial 
        ? `${BACKEND_URL}/api/testimonials/${editingTestimonial.id}`
        : `${BACKEND_URL}/api/testimonials`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testimonialForm)
      });
      
      if (res.ok) {
        await loadTestimonials();
        setEditingTestimonial(null);
        setShowTestimonialForm(false);
        setTestimonialForm({ person_name: '', person_image: '', position: '', cinema_name: '', quote: '', order: 0, is_active: true });
      }
    } catch (err) { console.error('Error saving testimonial:', err); }
    setSavingTestimonial(false);
  };

  const cancelTestimonialForm = () => {
    setEditingTestimonial(null);
    setShowTestimonialForm(false);
    setTestimonialForm({ person_name: '', person_image: '', position: '', cinema_name: '', quote: '', order: 0, is_active: true });
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('¿Eliminar este testimonio?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/testimonials/${id}`, { method: 'DELETE' });
      if (res.ok) await loadTestimonials();
    } catch (err) { console.error('Error deleting testimonial:', err); }
  };

  // Podcast functions
  const loadPodcasts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/podcasts`);
      if (res.ok) setPodcasts(await res.json());
    } catch (err) { console.error('Error loading podcasts:', err); }
  };

  const loadNavMenus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/navigation-menus`);
      if (res.ok) setNavMenus(await res.json());
    } catch (err) { console.error('Error loading nav menus:', err); }
  };

  const loadBravoCinemas = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/bravo-cinemas`);
      if (res.ok) setBravoCinemas(await res.json());
    } catch (err) { console.error('Error loading bravo cinemas:', err); }
  };

  const savePodcast = async () => {
    setSavingPodcast(true);
    try {
      const method = editingPodcast ? 'PUT' : 'POST';
      const url = editingPodcast 
        ? `${BACKEND_URL}/api/podcasts/${editingPodcast.id}`
        : `${BACKEND_URL}/api/podcasts`;
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(podcastForm)
      });
      
      if (res.ok) {
        await loadPodcasts();
        setEditingPodcast(null);
        setShowPodcastForm(false);
        setPodcastForm({ title: '', subtitle: '', image_url: '', spotify_url: '', is_enabled: true, is_locked: false, order: podcasts.length });
      }
    } catch (err) { console.error('Error saving podcast:', err); }
    setSavingPodcast(false);
  };

  const deletePodcast = async (id) => {
    if (!window.confirm('¿Eliminar este podcast?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/podcasts/${id}`, { method: 'DELETE' });
      if (res.ok) await loadPodcasts();
    } catch (err) { console.error('Error deleting podcast:', err); }
  };

  const togglePodcastEnabled = async (podcast) => {
    try {
      await fetch(`${BACKEND_URL}/api/podcasts/${podcast.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: !podcast.is_enabled })
      });
      await loadPodcasts();
    } catch (err) { console.error('Error toggling podcast:', err); }
  };

  const togglePodcastLocked = async (podcast) => {
    try {
      await fetch(`${BACKEND_URL}/api/podcasts/${podcast.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_locked: !podcast.is_locked })
      });
      await loadPodcasts();
    } catch (err) { console.error('Error toggling podcast lock:', err); }
  };

  const respondToInquiry = async () => {
    if (!selectedInquiry) return;
    setSavingInquiry(true);
    try {
      const body = {
        admin_response: inquiryResponse.admin_response,
        pricing_type: inquiryResponse.pricing_type
      };
      
      if (inquiryResponse.pricing_type === 'porcentaje_taquilla') {
        body.exhibitor_percentage = inquiryResponse.exhibitor_percentage;
      } else {
        body.flat_fee_price = inquiryResponse.flat_fee_price;
      }
      
      const res = await fetch(`${BACKEND_URL}/api/inquiries/${selectedInquiry.id}/respond`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        setSelectedInquiry(null);
        setInquiryResponse({
          admin_response: '',
          pricing_type: 'tanto_alzado',
          exhibitor_percentage: 50,
          flat_fee_price: 400
        });
        loadInquiries();
      }
    } catch (err) { console.error('Error responding to inquiry:', err); }
    setSavingInquiry(false);
  };

  const saveCinemaPricing = async (cinemaId) => {
    setSavingPricing(true);
    try {
      const body = {
        pricing_type: pricingForm.pricing_type
      };
      
      if (pricingForm.pricing_type === 'porcentaje_taquilla') {
        body.box_office = { exhibitor_percentage: pricingForm.exhibitor_percentage };
      } else {
        body.flat_fee = {
          directo_opera_ballet_concert: pricingForm.directo_opera_ballet_concert,
          encore_opera_ballet_concert: pricingForm.encore_opera_ballet_concert,
          grabado_opera_ballet_concert: pricingForm.grabado_opera_ballet_concert,
          pelicula: pricingForm.pelicula,
          documental: pricingForm.documental
        };
      }
      
      // Add Bravo rental mode
      body.bravo_rental = {
        mode: pricingForm.bravo_rental_mode,
        payment_type: pricingForm.bravo_rental_mode !== 'por_evento' ? pricingForm.bravo_rental_payment : null,
        price_per_event: pricingForm.bravo_rental_mode === 'por_evento' ? pricingForm.bravo_rental_price : 50
      };
      
      // Use pricingKey if available (for multi-venue cinemas)
      const pricingId = selectedCinema?.pricingKey || cinemaId;
      
      const res = await fetch(`${BACKEND_URL}/api/cinema-pricing/${pricingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        await loadCinemaPricings();
        // Update selected cinema in the list
        const updatedPricing = await res.json();
        setCinemaPricings(prev => ({ ...prev, [pricingId]: updatedPricing }));
        // Close modal after successful save
        setShowCinemaDetail(false);
        setSelectedCinema(null);
      }
    } catch (err) { console.error('Error saving pricing:', err); }
    setSavingPricing(false);
  };

  const loadContents = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content`);
      if (res.ok) {
        const data = await res.json();
        setContents(data);
      }
    } catch (err) { console.error('Error loading contents:', err); }
  };

  // RAG Documents functions
  const loadRagDocuments = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/rag/documents`);
      if (res.ok) {
        const data = await res.json();
        setRagDocuments(data.documents || []);
        setRagStats(data.stats || { total_chunks: 0, documents_count: 0 });
      }
    } catch (err) { console.error('Error loading RAG documents:', err); }
  };

  const handleUploadRagDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setDocUploadError('Solo se permiten archivos PDF');
      return;
    }

    setUploadingDoc(true);
    setDocUploadError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_name', file.name.replace('.pdf', ''));

    try {
      const res = await fetch(`${BACKEND_URL}/api/rag/upload-document`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Documento "${data.document_name}" procesado (${data.chunks_count} fragmentos)`);
        loadRagDocuments();
      } else {
        let errorMsg = 'Error al procesar el documento';
        try {
          const error = await res.json();
          errorMsg = error.detail || errorMsg;
        } catch (parseErr) {
          // Response wasn't JSON
        }
        setDocUploadError(errorMsg);
      }
    } catch (err) {
      console.error('Error uploading RAG document:', err);
      setDocUploadError(`Error: ${err.message || 'No se pudo subir el documento'}`);
    } finally {
      setUploadingDoc(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleDeleteRagDocument = async (docId, docName) => {
    if (!window.confirm(`¿Eliminar "${docName}" de la base de conocimiento?`)) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/rag/documents/${docId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Documento eliminado');
        loadRagDocuments();
      }
    } catch (err) {
      console.error('Error deleting RAG document:', err);
    }
  };

  // Analytics functions
  const loadAnalytics = async (days = 30) => {
    setLoadingAnalytics(true);
    try {
      const [dashboardRes, realtimeRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/analytics/dashboard?days=${days}`),
        fetch(`${BACKEND_URL}/api/analytics/realtime`)
      ]);
      if (dashboardRes.ok) setAnalyticsData(await dashboardRes.json());
      if (realtimeRes.ok) setRealtimeData(await realtimeRes.json());
    } catch (err) { console.error('Error loading analytics:', err); }
    setLoadingAnalytics(false);
  };

  // Load analytics when tab is active
  useEffect(() => {
    if (activeSection === 'analytics' && !analyticsData) {
      loadAnalytics(analyticsPeriod);
    }
  }, [activeSection, analyticsPeriod]);

  // Load RAG documents when tab is active
  useEffect(() => {
    if (activeSection === 'rag-documents') {
      loadRagDocuments();
    }
  }, [activeSection]);

  // Refresh realtime data every 30 seconds when on analytics tab
  useEffect(() => {
    if (activeSection !== 'analytics') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/analytics/realtime`);
        if (res.ok) setRealtimeData(await res.json());
      } catch (err) { console.error('Error refreshing realtime:', err); }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeSection]);

  const loadContentMedia = async (contentId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/media/content/${contentId}`);
      if (res.ok) {
        const data = await res.json();
        setContentMedia(data);
      }
    } catch (err) { console.error('Error loading media:', err); }
  };

  const deleteContent = async (contentId) => {
    if (!window.confirm('¿Estás seguro de que deseas ELIMINAR este contenido permanentemente?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${contentId}`, { method: 'DELETE' });
      if (res.ok) {
        loadContents();
      } else {
        const data = await res.json();
        alert(data.detail || 'Error al eliminar contenido');
      }
    } catch (err) { console.error('Error:', err); }
  };

  // Cinema management functions (delete/deactivate)
  const deactivateCinema = async (cinemaId) => {
    if (!window.confirm('¿Estás seguro de que deseas INACTIVAR este cine? El cine ya no podrá acceder al portal.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}/deactivate`, { method: 'PUT' });
      if (res.ok) {
        loadData();
        setShowCinemaDetail(false);
      } else {
        const data = await res.json();
        alert(data.detail || 'Error al inactivar cine');
      }
    } catch (err) { console.error('Error:', err); }
  };

  const reactivateCinema = async (cinemaId) => {
    if (!window.confirm('¿Estás seguro de que deseas REACTIVAR este cine?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}/reactivate`, { method: 'PUT' });
      if (res.ok) {
        loadData();
        setShowCinemaDetail(false);
      } else {
        const data = await res.json();
        alert(data.detail || 'Error al reactivar cine');
      }
    } catch (err) { console.error('Error:', err); }
  };

  const deleteCinema = async (cinemaId) => {
    if (!window.confirm('¿Estás seguro de que deseas ELIMINAR este cine PERMANENTEMENTE? Esta acción no se puede deshacer.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}`, { method: 'DELETE' });
      if (res.ok) {
        loadData();
        setShowCinemaDetail(false);
      } else {
        const data = await res.json();
        alert(data.detail || 'Error al eliminar cine');
      }
    } catch (err) { console.error('Error:', err); }
  };

  const openContentEditor = (content = null) => {
    setSelectedContentForEdit(content);
    setShowContentEditor(true);
  };

  const loadSections = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sections`);
      if (res.ok) {
        const data = await res.json();
        setHomeSections(data.sort((a, b) => a.order - b.order));
      }
    } catch (err) { console.error('Error loading sections:', err); }
  };

  const loadContentRestrictions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content`);
      if (res.ok) {
        const contents = await res.json();
        const restrictions = {};
        contents.forEach(c => { restrictions[c.id] = c.is_restricted || false; });
        setContentRestrictions(restrictions);
      }
    } catch (err) { console.error('Error loading restrictions:', err); }
  };

  // Hero Feature functions
  const createFeature = async () => {
    if (!newFeatureLabel.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          icon: newFeatureIcon,
          label: newFeatureLabel,
          order: heroFeatures.length + 1
        })
      });
      if (res.ok) {
        setNewFeatureLabel('');
        loadHeroData();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const updateFeature = async (featureId, updates) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-features/${featureId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        setEditingFeature(null);
        loadHeroData();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const deleteFeature = async (featureId) => {
    if (!window.confirm('¿Eliminar este icono?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-features/${featureId}`, { method: 'DELETE' });
      if (res.ok) loadHeroData();
    } catch (err) { console.error('Error:', err); }
  };

  // Hero Slide functions
  const updateSlide = async (slideId, updates) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-slides/${slideId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        loadHeroData();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const deleteSlide = async (slideId) => {
    if (!window.confirm('¿Eliminar este slide?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-slides/${slideId}`, { method: 'DELETE' });
      if (res.ok) loadHeroData();
    } catch (err) { console.error('Error:', err); }
  };

  const createSlide = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/hero-slides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opera: 'Nuevo Slide',
          composer: 'Compositor',
          subtitle: 'Subtítulo',
          tagline: 'ETIQUETA',
          image: '',
          order: heroSlides.length,
          is_active: true
        })
      });
      if (res.ok) {
        const newSlide = await res.json();
        loadHeroData();
        setEditingSlide(newSlide.id);
        toast.success('Slide creado. Edita los campos.');
      }
    } catch (err) { console.error('Error:', err); }
  };

  const toggleContentRestriction = async (contentId) => {
    const newValue = !contentRestrictions[contentId];
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${contentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_restricted: newValue })
      });
      if (res.ok) {
        setContentRestrictions(prev => ({ ...prev, [contentId]: newValue }));
      }
    } catch (err) { console.error('Error:', err); }
  };

  const createSection = async () => {
    if (!newSectionName.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newSectionName,
          type: newSectionType,
          order: homeSections.length + 1
        })
      });
      if (res.ok) {
        setNewSectionName('');
        loadSections();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const updateSection = async (sectionId, updates) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sections/${sectionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        setEditingSection(null);
        loadSections();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('¿Eliminar esta sección?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sections/${sectionId}`, { method: 'DELETE' });
      if (res.ok) loadSections();
    } catch (err) { console.error('Error:', err); }
  };

  const moveSection = async (sectionId, direction) => {
    const idx = homeSections.findIndex(s => s.id === sectionId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === homeSections.length - 1)) return;
    
    const newOrder = direction === 'up' ? homeSections[idx - 1].order : homeSections[idx + 1].order;
    const otherIdx = direction === 'up' ? idx - 1 : idx + 1;
    const currentOrder = homeSections[idx].order;
    
    await updateSection(sectionId, { order: newOrder });
    await updateSection(homeSections[otherIdx].id, { order: currentOrder });
  };

  // Section content management functions
  const loadSectionContents = async (sectionId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sections/${sectionId}/contents`);
      if (res.ok) {
        const data = await res.json();
        setSectionContents(prev => ({ ...prev, [sectionId]: data }));
      }
    } catch (err) { console.error('Error loading section contents:', err); }
  };

  const addContentToSection = async (sectionId, contentId) => {
    const section = homeSections.find(s => s.id === sectionId);
    if (!section) return;
    
    const currentContents = section.contents || [];
    const alreadyExists = currentContents.some(c => c.content_id === contentId);
    if (alreadyExists) return;
    
    const newContents = [...currentContents, { content_id: contentId, is_visible: true }];
    await updateSection(sectionId, { contents: newContents });
    loadSectionContents(sectionId);
  };

  const removeContentFromSection = async (sectionId, contentId) => {
    const section = homeSections.find(s => s.id === sectionId);
    if (!section) return;
    
    const currentContents = section.contents || [];
    const newContents = currentContents.filter(c => c.content_id !== contentId);
    await updateSection(sectionId, { contents: newContents });
    loadSectionContents(sectionId);
  };

  const toggleContentVisibility = async (sectionId, contentId) => {
    const section = homeSections.find(s => s.id === sectionId);
    if (!section) return;
    
    const currentContents = section.contents || [];
    const newContents = currentContents.map(c => 
      c.content_id === contentId ? { ...c, is_visible: !c.is_visible } : c
    );
    await updateSection(sectionId, { contents: newContents });
    loadSectionContents(sectionId);
  };

  // Move content up or down within a section
  const moveContentInSection = async (sectionId, currentIndex, direction) => {
    const section = homeSections.find(s => s.id === sectionId);
    if (!section) return;
    
    const currentContents = [...(section.contents || [])];
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Boundary check
    if (newIndex < 0 || newIndex >= currentContents.length) return;
    
    // Swap positions
    const temp = currentContents[currentIndex];
    currentContents[currentIndex] = currentContents[newIndex];
    currentContents[newIndex] = temp;
    
    await updateSection(sectionId, { contents: currentContents });
    loadSectionContents(sectionId);
  };

  const toggleExpandSection = (sectionId) => {
    if (expandedSection === sectionId) {
      setExpandedSection(null);
    } else {
      setExpandedSection(sectionId);
      if (!sectionContents[sectionId]) {
        loadSectionContents(sectionId);
      }
    }
  };

  const getAvailableContentsForSection = (sectionId) => {
    const section = homeSections.find(s => s.id === sectionId);
    const currentContentIds = (section?.contents || []).map(c => c.content_id);
    // Filter: not already in section + match section_id if set + match search query
    return contents.filter(c => {
      const notInSection = !currentContentIds.includes(c.id);
      const belongsToSection = !c.section_id || c.section_id === sectionId;
      const matchesSearch = !contentSearchQuery || 
        c.title?.toLowerCase().includes(contentSearchQuery.toLowerCase()) ||
        c.composer?.toLowerCase().includes(contentSearchQuery.toLowerCase());
      return notInSection && belongsToSection && matchesSearch;
    });
  };

  const updateCinemaStatus = async (cinemaId, status, venueIndex = null) => {
    try {
      // If venueIndex is provided, use the venue-specific endpoint
      const url = venueIndex !== null 
        ? `${BACKEND_URL}/api/cinemas/${cinemaId}/venue/${venueIndex}/status?status=${status}`
        : `${BACKEND_URL}/api/cinemas/${cinemaId}/status?status=${status}`;
      const res = await fetch(url, { method: 'PUT' });
      if (res.ok) loadData();
    } catch (err) { console.error('Error:', err); }
  };

  // Duplicate content function
  const duplicateContent = async (contentId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/content/${contentId}/duplicate`, { method: 'POST' });
      if (res.ok) {
        const newContent = await res.json();
        loadContents();
        // Open editor with the new content so user can modify it
        openContentEditor(newContent);
      }
    } catch (err) { console.error('Error duplicating content:', err); }
  };

  const assignBravoNumber = async () => {
    if (!selectedCinema || !bravoNumber.trim()) return;
    setSavingBravo(true);
    try {
      // Use venue-specific endpoint if cinema has multiple venues
      const hasMultipleVenues = selectedCinema.venues && selectedCinema.venues.length > 0;
      const venueIndex = selectedCinema.selectedVenueIndex;
      const url = hasMultipleVenues && venueIndex !== undefined
        ? `${BACKEND_URL}/api/cinemas/${selectedCinema.id}/venue/${venueIndex}/bravo-number?bravo_number=${encodeURIComponent(bravoNumber)}`
        : `${BACKEND_URL}/api/cinemas/${selectedCinema.id}/bravo-number?bravo_number=${encodeURIComponent(bravoNumber)}`;
      
      const res = await fetch(url, { method: 'PUT' });
      if (res.ok) {
        setShowBravoModal(false);
        setBravoNumber('');
        // Refresh the cinema data
        const updatedCinema = await fetch(`${BACKEND_URL}/api/cinemas/${selectedCinema.id}`).then(r => r.json());
        setSelectedCinema(updatedCinema);
        loadData();
      }
    } catch (err) { console.error('Error:', err); }
    setSavingBravo(false);
  };

  const approveProfileChanges = async (cinemaId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}/approve-changes`, { method: 'PUT' });
      if (res.ok) {
        // Refresh data
        const updatedCinema = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}`).then(r => r.json());
        setSelectedCinema(updatedCinema);
        loadData();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const rejectProfileChanges = async (cinemaId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}/reject-changes`, { method: 'PUT' });
      if (res.ok) {
        const updatedCinema = await fetch(`${BACKEND_URL}/api/cinemas/${cinemaId}`).then(r => r.json());
        setSelectedCinema(updatedCinema);
        loadData();
      }
    } catch (err) { console.error('Error:', err); }
  };

  const updateBookingStatus = async (bookingId, status, notes = '') => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_notes: notes })
      });
      if (res.ok) loadData();
    } catch (err) { console.error('Error:', err); }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    approved: 'bg-green-500/20 text-green-400 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
    confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  const statusLabels = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado', confirmed: 'Confirmado', inactive: 'Inactivo' };

  const pendingInquiriesCount = inquiries.filter(i => i.status === 'pending').length;
  
  const [expandedMenu, setExpandedMenu] = useState('dashboard');
  
  const menuStructure = [
    { 
      id: 'dashboard-group', 
      label: 'Dashboard', 
      icon: LayoutDashboard,
      items: [
        { id: 'screenings', label: 'Pases', icon: BarChart3 },
        { id: 'analytics', label: 'Analíticas', icon: Monitor },
        { id: 'master', label: 'Master', icon: BookOpen }
      ]
    },
    { 
      id: 'design-group', 
      label: 'Diseño', 
      icon: Layers,
      items: [
        { id: 'hero', label: 'Hero/Slider', icon: Image },
        { id: 'testimonials', label: 'Reseñas', icon: Star },
        { id: 'podcasts', label: 'Podcasts', icon: Mic },
        { id: 'sections', label: 'Secciones Home', icon: Layers },
        { id: 'content', label: 'Contenidos', icon: Film },
        { id: 'navigation', label: 'Menú/Nav', icon: Globe }
      ]
    },
    { 
      id: 'commercial-group', 
      label: 'Comercial', 
      icon: Building2,
      items: [
        { id: 'cinemas', label: 'Cines', icon: Building2, badge: stats?.pending_cinemas },
        { id: 'bravo-network', label: 'Red Bravo', icon: Radio },
        { id: 'conditions', label: 'Condiciones', icon: DollarSign },
        { id: 'bookings', label: 'Reservas', icon: Calendar, badge: stats?.pending_bookings },
        { id: 'change-requests', label: 'Solicitudes', icon: AlertCircle, badge: pendingChangeRequestsCount },
        { id: 'inquiries', label: 'Consultas', icon: HelpCircle, badge: pendingInquiriesCount },
        { id: 'turandot-inscripciones', label: 'Inscripciones', icon: Users }
      ]
    },
    { 
      id: 'ai-group', 
      label: 'IA/Chat', 
      icon: Bot,
      items: [
        { id: 'rag-documents', label: 'Documentos', icon: FileText },
        { id: 'chat-preview', label: 'Preview Chat', icon: MessageSquare }
      ]
    }
  ];

  const sectionTypeLabels = {
    content_carousel: 'Carrusel de contenidos',
    detail_carousel: 'Carrusel detallado',
    concert_carousel: 'Carrusel de conciertos'
  };

  if (!admin) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><p className="text-white">Cargando...</p></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      <aside className="w-64 bg-[#0f0f0f] border-r border-white/5 flex flex-col">
        <div className="p-6 border-b border-white/5">
          <Link to="/"><img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-8 w-auto" /></Link>
          <p className="text-white/40 text-xs mt-2">Panel de Administración</p>
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          {menuStructure.map((group) => (
            <div key={group.id} className="mb-2">
              <button 
                onClick={() => setExpandedMenu(expandedMenu === group.id ? null : group.id)} 
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg ${expandedMenu === group.id ? 'bg-white/10 text-white' : 'text-white/60 hover:bg-white/5'}`}
              >
                <div className="flex items-center gap-3">
                  <group.icon className="w-5 h-5" />
                  <span className="text-sm font-semibold">{group.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${expandedMenu === group.id ? 'rotate-180' : ''}`} />
              </button>
              {expandedMenu === group.id && (
                <div className="mt-1 ml-4 border-l border-white/10 pl-2">
                  {group.items.map((item) => (
                    <button 
                      key={item.id} 
                      onClick={() => setActiveSection(item.id)} 
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg mb-1 ${activeSection === item.id ? 'bg-[#e4002b] text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'}`}
                    >
                      <div className="flex items-center gap-2">
                        <item.icon className="w-4 h-4" />
                        <span className="text-xs font-medium">{item.label}</span>
                      </div>
                      {item.badge > 0 && <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded-full">{item.badge}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center justify-between">
            <div><p className="text-white text-sm font-medium">{admin.name}</p><p className="text-white/40 text-xs">{admin.role}</p></div>
            <button onClick={() => { localStorage.removeItem('admin_session'); navigate('/admin/login'); }} className="p-2 text-white/40 hover:text-white"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <header className="sticky top-0 z-10 bg-[#0a0a0a]/80 backdrop-blur-sm border-b border-white/5 px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-white text-xl font-bold capitalize">{activeSection}</h1>
            <button className="relative p-2 text-white/40 hover:text-white">
              <Bell className="w-5 h-5" />
              {(stats?.pending_cinemas + stats?.pending_bookings) > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-[#e4002b] rounded-full" />}
            </button>
          </div>
        </header>

        <div className="p-8">
          {activeSection === 'dashboard' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-[#141414] rounded-xl p-6"><Film className="w-8 h-8 text-[#e4002b] mb-4" /><p className="text-white text-3xl font-bold">{stats?.content_count || 0}</p><p className="text-white/50 text-sm">Contenidos</p></div>
                <div className="bg-[#141414] rounded-xl p-6"><Building2 className="w-8 h-8 text-blue-400 mb-4" /><p className="text-white text-3xl font-bold">{stats?.cinema_count || 0}</p><p className="text-white/50 text-sm">Cines registrados</p></div>
                <div className="bg-[#141414] rounded-xl p-6"><Calendar className="w-8 h-8 text-yellow-400 mb-4" /><p className="text-white text-3xl font-bold">{stats?.pending_cinemas || 0}</p><p className="text-white/50 text-sm">Cines pendientes</p></div>
                <div className="bg-[#141414] rounded-xl p-6"><Calendar className="w-8 h-8 text-green-400 mb-4" /><p className="text-white text-3xl font-bold">{stats?.booking_count || 0}</p><p className="text-white/50 text-sm">Reservas totales</p></div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-[#141414] rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-4">Cines pendientes</h3>
                  {cinemas.filter(c => c.status === 'pending').length === 0 ? <p className="text-white/40 text-sm">No hay cines pendientes</p> : (
                    <div className="space-y-3">
                      {cinemas.filter(c => c.status === 'pending').slice(0, 5).map(c => (
                        <div key={c.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div><p className="text-white font-medium">{c.venue_name}</p><p className="text-white/50 text-sm">{c.company_name}</p></div>
                          <div className="flex gap-2">
                            <button onClick={() => updateCinemaStatus(c.id, 'approved')} className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"><Check className="w-4 h-4" /></button>
                            <button onClick={() => updateCinemaStatus(c.id, 'rejected')} className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"><X className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-[#141414] rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-4">Reservas pendientes</h3>
                  {bookings.filter(b => b.status === 'pending').length === 0 ? <p className="text-white/40 text-sm">No hay reservas pendientes</p> : (
                    <div className="space-y-3">
                      {bookings.filter(b => b.status === 'pending').slice(0, 5).map(b => {
                        const content = contents.find(c => c.id === b.content_id);
                        const cinema = cinemas.find(c => c.id === b.cinema_id);
                        return (
                          <div key={b.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                            <div><p className="text-white font-medium">{content?.title || 'N/A'}</p><p className="text-white/50 text-sm">{cinema?.venue_name || 'N/A'}</p></div>
                            <div className="flex gap-2">
                              <button onClick={() => updateBookingStatus(b.id, 'approved')} className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30"><Check className="w-4 h-4" /></button>
                              <button onClick={() => updateBookingStatus(b.id, 'rejected')} className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"><X className="w-4 h-4" /></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'screenings' && (
            <ScreeningsDashboard />
          )}

          {activeSection === 'hero' && (
            <div>
              <div className="mb-6">
                <h2 className="text-white text-xl font-semibold mb-2">Gestionar Hero / Slider</h2>
                <p className="text-white/50 text-sm">Configura los slides del hero y los iconos de características</p>
              </div>

              {/* Hero Features (Icons) */}
              <div className="bg-[#141414] rounded-xl p-6 mb-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#e4002b]" />
                  Iconos de características
                </h3>
                
                {/* Add new feature */}
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-white/5 rounded-lg">
                  <select
                    value={newFeatureIcon}
                    onChange={(e) => setNewFeatureIcon(e.target.value)}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                  >
                    {AVAILABLE_ICONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Texto del icono (ej: Subtítulos)"
                    value={newFeatureLabel}
                    onChange={(e) => setNewFeatureLabel(e.target.value)}
                    className="flex-1 min-w-[200px] px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                  />
                  <button
                    onClick={createFeature}
                    disabled={!newFeatureLabel.trim()}
                    className="flex items-center gap-2 px-6 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" /> Añadir
                  </button>
                </div>

                {/* Features list */}
                <div className="space-y-2">
                  {heroFeatures.map((feature) => (
                    <div key={feature.id} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                      <div className="w-10 h-10 rounded-full border border-white/30 flex items-center justify-center text-white/80">
                        {feature.icon.charAt(0)}
                      </div>
                      
                      {editingFeature === feature.id ? (
                        <>
                          <select
                            defaultValue={feature.icon}
                            onChange={(e) => updateFeature(feature.id, { icon: e.target.value })}
                            className="px-3 py-1 bg-white/10 border border-[#e4002b] rounded text-white"
                          >
                            {AVAILABLE_ICONS.map(icon => (
                              <option key={icon} value={icon}>{icon}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            defaultValue={feature.label}
                            onBlur={(e) => updateFeature(feature.id, { label: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && updateFeature(feature.id, { label: e.target.value })}
                            className="flex-1 px-3 py-1 bg-white/10 border border-[#e4002b] rounded text-white"
                          />
                        </>
                      ) : (
                        <>
                          <span className="text-white/50 text-sm w-24">{feature.icon}</span>
                          <span className="flex-1 text-white">{feature.label}</span>
                        </>
                      )}
                      
                      <button
                        onClick={() => updateFeature(feature.id, { is_active: !feature.is_active })}
                        className={`px-3 py-1 rounded text-sm ${feature.is_active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                      >
                        {feature.is_active ? 'Activo' : 'Inactivo'}
                      </button>
                      
                      <button
                        onClick={() => setEditingFeature(editingFeature === feature.id ? null : feature.id)}
                        className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded"
                      >
                        {editingFeature === feature.id ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => deleteFeature(feature.id)}
                        className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hero Slides */}
              <div className="bg-[#141414] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Image className="w-5 h-5 text-[#e4002b]" />
                    Slides del Hero ({heroSlides.length})
                  </h3>
                  <button
                    onClick={createSlide}
                    className="flex items-center gap-2 px-4 py-2 bg-[#e4002b] text-white text-sm rounded-lg hover:bg-[#c70024] transition-colors"
                    data-testid="add-slide-btn"
                  >
                    <Plus className="w-4 h-4" /> Añadir Slide
                  </button>
                </div>
                
                <div className="space-y-4">
                  {heroSlides.map((slide, slideIndex) => (
                    <div key={slide.id} className="bg-white/5 rounded-lg overflow-hidden">
                      {/* Slide header bar */}
                      <div className="flex items-center gap-3 px-4 py-3 bg-white/5 border-b border-white/10">
                        {/* Order arrows */}
                        <div className="flex flex-col gap-0.5">
                          <button
                            disabled={slideIndex === 0}
                            onClick={async () => {
                              const newSlides = [...heroSlides];
                              [newSlides[slideIndex - 1], newSlides[slideIndex]] = [newSlides[slideIndex], newSlides[slideIndex - 1]];
                              const ids = newSlides.map(s => s.id);
                              try {
                                await fetch(`${BACKEND_URL}/api/hero-slides/reorder`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(ids)
                                });
                                loadHeroData();
                              } catch (err) { console.error(err); }
                            }}
                            className="p-0.5 text-white/30 hover:text-white disabled:opacity-20"
                            title="Subir"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            disabled={slideIndex === heroSlides.length - 1}
                            onClick={async () => {
                              const newSlides = [...heroSlides];
                              [newSlides[slideIndex], newSlides[slideIndex + 1]] = [newSlides[slideIndex + 1], newSlides[slideIndex]];
                              const ids = newSlides.map(s => s.id);
                              try {
                                await fetch(`${BACKEND_URL}/api/hero-slides/reorder`, {
                                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(ids)
                                });
                                loadHeroData();
                              } catch (err) { console.error(err); }
                            }}
                            className="p-0.5 text-white/30 hover:text-white disabled:opacity-20"
                            title="Bajar"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="h-10 w-16 bg-cover bg-center rounded" style={{ backgroundImage: `url(${slide.image})` }} />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-bold text-sm truncate">{slide.opera}</h4>
                          <p className="text-white/50 text-xs truncate">{slide.composer} · Orden: {slide.order ?? slideIndex}</p>
                        </div>
                        <button
                          onClick={() => updateSlide(slide.id, { is_active: !slide.is_active })}
                          className={`px-3 py-1 rounded text-xs font-medium ${slide.is_active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                        >
                          {slide.is_active ? 'Activo' : 'Inactivo'}
                        </button>
                        <button
                          onClick={() => {
                            if (editingSlide === slide.id) {
                              setEditingSlide(null);
                              setSlideForm({});
                            } else {
                              setEditingSlide(slide.id);
                              setSlideForm({
                                tagline: slide.tagline || '',
                                opera: slide.opera || '',
                                composer: slide.composer || '',
                                subtitle: slide.subtitle || '',
                                availability_text: slide.availability_text || '',
                                image: slide.image || '',
                                content_id: slide.content_id || '',
                                buttons: slide.buttons || []
                              });
                            }
                          }}
                          className={`p-2 rounded ${editingSlide === slide.id ? 'bg-[#e4002b] text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
                        >
                          {editingSlide === slide.id ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteSlide(slide.id)}
                          className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Edit form (expanded) */}
                      {editingSlide === slide.id && (
                        <div className="p-5 space-y-4 bg-[#0d0d0d]">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Etiqueta superior</label>
                              <input type="text" value={slideForm.tagline || ''} onChange={(e) => setSlideForm(f => ({ ...f, tagline: e.target.value }))} placeholder="Ej: CONTRATA VERSIÓN DIGITAL" className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Título principal</label>
                              <input type="text" value={slideForm.opera || ''} onChange={(e) => setSlideForm(f => ({ ...f, opera: e.target.value }))} placeholder="Ej: L'ELISIR D'AMORE" className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Compositor / Autor</label>
                              <input type="text" value={slideForm.composer || ''} onChange={(e) => setSlideForm(f => ({ ...f, composer: e.target.value }))} placeholder="Ej: Donizetti" className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Subtítulo</label>
                              <input type="text" value={slideForm.subtitle || ''} onChange={(e) => setSlideForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="Ej: ESTRENO DIGITAL 14/12/2025" className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Texto de disponibilidad</label>
                              <input type="text" value={slideForm.availability_text || ''} onChange={(e) => setSlideForm(f => ({ ...f, availability_text: e.target.value }))} placeholder="Ej: DISPONIBLE PARA CONTRATACIÓN EN CINES" className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm" />
                            </div>
                            <div>
                              <label className="block text-white/60 text-xs mb-1">Vincular a contenido</label>
                              <select value={slideForm.content_id || ''} onChange={(e) => setSlideForm(f => ({ ...f, content_id: e.target.value || null }))} className="w-full px-3 py-2 bg-[#1a1a1a] border border-white/20 rounded text-white text-sm">
                                <option value="">Sin vincular</option>
                                {(contents || []).map(c => (
                                  <option key={c.id} value={c.id}>{c.title} - {c.composer}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-white/60 text-xs mb-1">URL de imagen de fondo</label>
                            <input type="text" value={slideForm.image || ''} onChange={(e) => setSlideForm(f => ({ ...f, image: e.target.value }))} placeholder="https://..." className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm font-mono" />
                            {slideForm.image && <div className="mt-2 h-24 bg-cover bg-center rounded border border-white/10" style={{ backgroundImage: `url(${slideForm.image})` }} />}
                          </div>

                          {/* Buttons config */}
                          <div className="pt-3 border-t border-white/10">
                            <label className="block text-white/70 text-sm font-medium mb-3">Botones del Slide</label>
                            <div className="space-y-2">
                              {(slideForm.buttons || []).map((btn, btnIdx) => (
                                <div key={btn.id || btnIdx} className="bg-white/5 rounded-lg p-3">
                                  <div className="grid grid-cols-4 gap-2 items-end">
                                    <div>
                                      <label className="block text-white/40 text-xs mb-1">Texto</label>
                                      <input type="text" value={btn.label || ''} onChange={(e) => { const nb = [...(slideForm.buttons || [])]; nb[btnIdx] = { ...nb[btnIdx], label: e.target.value }; setSlideForm(f => ({ ...f, buttons: nb })); }} className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded text-white text-xs" />
                                    </div>
                                    <div>
                                      <label className="block text-white/40 text-xs mb-1">Estilo</label>
                                      <select value={btn.type || 'primary'} onChange={(e) => { const nb = [...(slideForm.buttons || [])]; nb[btnIdx] = { ...nb[btnIdx], type: e.target.value }; setSlideForm(f => ({ ...f, buttons: nb })); }} className="w-full px-2 py-1.5 bg-[#1a1a1a] border border-white/20 rounded text-white text-xs">
                                        <option value="primary">Primario (rojo)</option>
                                        <option value="secondary">Secundario</option>
                                      </select>
                                    </div>
                                    <div>
                                      <label className="block text-white/40 text-xs mb-1">Acción</label>
                                      <select value={btn.action || 'contract'} onChange={(e) => { const nb = [...(slideForm.buttons || [])]; nb[btnIdx] = { ...nb[btnIdx], action: e.target.value }; setSlideForm(f => ({ ...f, buttons: nb })); }} className="w-full px-2 py-1.5 bg-[#1a1a1a] border border-white/20 rounded text-white text-xs">
                                        <option value="contract">Ir a contratar</option>
                                        <option value="content">Ver ficha</option>
                                        <option value="link">Enlace personalizado</option>
                                      </select>
                                    </div>
                                    <div className="flex gap-1">
                                      <label className="flex items-center gap-1 cursor-pointer">
                                        <input type="checkbox" checked={btn.is_active !== false} onChange={(e) => { const nb = [...(slideForm.buttons || [])]; nb[btnIdx] = { ...nb[btnIdx], is_active: e.target.checked }; setSlideForm(f => ({ ...f, buttons: nb })); }} className="w-3 h-3 accent-[#e4002b]" />
                                        <span className="text-white/50 text-xs">On</span>
                                      </label>
                                      <button onClick={() => { const nb = (slideForm.buttons || []).filter((_, i) => i !== btnIdx); setSlideForm(f => ({ ...f, buttons: nb })); }} className="p-1 text-red-400/50 hover:text-red-400 rounded"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                  </div>
                                  {btn.action === 'link' && (
                                    <div className="mt-2">
                                      <input type="text" value={btn.link || ''} onChange={(e) => { const nb = [...(slideForm.buttons || [])]; nb[btnIdx] = { ...nb[btnIdx], link: e.target.value }; setSlideForm(f => ({ ...f, buttons: nb })); }} placeholder="https://... o /ruta" className="w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded text-white text-xs" />
                                    </div>
                                  )}
                                </div>
                              ))}
                              <button onClick={() => setSlideForm(f => ({ ...f, buttons: [...(f.buttons || []), { id: String(Date.now()), label: 'NUEVO', type: 'secondary', action: 'link', link: '', is_active: true, order: (f.buttons || []).length }] }))} className="w-full py-2 border border-dashed border-white/20 text-white/50 hover:text-white rounded text-xs flex items-center justify-center gap-1">
                                <Plus className="w-3 h-3" /> Añadir botón
                              </button>
                            </div>
                          </div>

                          {/* Save / Cancel */}
                          <div className="flex gap-3 pt-3 border-t border-white/10">
                            <button
                              onClick={async () => {
                                await updateSlide(slide.id, slideForm);
                                setEditingSlide(null);
                                setSlideForm({});
                                toast.success('Slide guardado');
                              }}
                              className="px-5 py-2 bg-[#e4002b] text-white text-sm font-medium rounded-lg hover:bg-[#c70024] transition-colors flex items-center gap-2"
                            >
                              <Save className="w-4 h-4" /> Guardar cambios
                            </button>
                            <button
                              onClick={() => { setEditingSlide(null); setSlideForm({}); }}
                              className="px-5 py-2 border border-white/20 text-white/60 text-sm rounded-lg hover:bg-white/10 transition-colors"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Collapsed info */}
                      {editingSlide !== slide.id && (
                        <div className="px-4 py-3">
                          <div className="flex items-center gap-3 text-xs text-white/50">
                            {slide.tagline && <span className="text-[#e4002b]">{slide.tagline}</span>}
                            <span>{slide.subtitle}</span>
                            {slide.content_id ? (
                              <span className="text-green-400 flex items-center gap-1"><Link2 className="w-3 h-3" />Vinculado: {(contents || []).find(c => c.id === slide.content_id)?.title || '?'}</span>
                            ) : (
                              <span className="text-yellow-400/70 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Sin vincular</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Testimonials Section */}
          {activeSection === 'testimonials' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-white text-xl font-semibold">Gestionar Reseñas</h2>
                  <p className="text-white/50 text-sm">Testimonios que aparecen en la página principal</p>
                </div>
                <button
                  onClick={() => {
                    setEditingTestimonial(null);
                    setShowTestimonialForm(true);
                    setTestimonialForm({ person_name: '', person_image: '', position: '', cinema_name: '', quote: '', order: testimonials.length, is_active: true });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024]"
                >
                  <Plus className="w-4 h-4" /> Nueva Reseña
                </button>
              </div>

              {/* Testimonial Form */}
              {showTestimonialForm && (
                <div className="bg-[#141414] rounded-xl p-6 mb-6">
                  <h3 className="text-white font-semibold mb-4">{editingTestimonial ? 'Editar Reseña' : 'Nueva Reseña'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Nombre de la persona *</label>
                      <input
                        type="text"
                        value={testimonialForm.person_name}
                        onChange={(e) => setTestimonialForm({...testimonialForm, person_name: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                        placeholder="Juan García"
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">URL de la imagen (círculo)</label>
                      <input
                        type="text"
                        value={testimonialForm.person_image}
                        onChange={(e) => setTestimonialForm({...testimonialForm, person_image: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Cargo *</label>
                      <input
                        type="text"
                        value={testimonialForm.position}
                        onChange={(e) => setTestimonialForm({...testimonialForm, position: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                        placeholder="Director, Gerente, Programador..."
                      />
                    </div>
                    <div>
                      <label className="text-white/50 text-xs mb-1 block">Nombre del cine *</label>
                      <input
                        type="text"
                        value={testimonialForm.cinema_name}
                        onChange={(e) => setTestimonialForm({...testimonialForm, cinema_name: e.target.value})}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                        placeholder="Cines Verdi"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-white/50 text-xs mb-1 block">Testimonio *</label>
                    <textarea
                      value={testimonialForm.quote}
                      onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                      rows={3}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none resize-none"
                      placeholder="La calidad de los contenidos de Bravo es excepcional..."
                    />
                  </div>
                  <div className="flex items-center gap-6 mb-4">
                    <div className="flex items-center gap-2">
                      <label className="text-white/50 text-xs">Orden:</label>
                      <input
                        type="number"
                        value={testimonialForm.order}
                        onChange={(e) => setTestimonialForm({...testimonialForm, order: parseInt(e.target.value) || 0})}
                        className="w-20 px-2 py-1 bg-white/5 border border-white/10 rounded text-white text-sm text-center"
                      />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={testimonialForm.is_active}
                        onChange={(e) => setTestimonialForm({...testimonialForm, is_active: e.target.checked})}
                        className="w-4 h-4 rounded border-white/10 bg-white/5 text-[#e4002b] focus:ring-[#e4002b]"
                      />
                      <span className="text-white/70 text-sm">Activo</span>
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={cancelTestimonialForm}
                      className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 text-sm"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveTestimonial}
                      disabled={!testimonialForm.person_name || !testimonialForm.quote || !testimonialForm.position || !testimonialForm.cinema_name || savingTestimonial}
                      className="px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] disabled:opacity-50 text-sm font-medium"
                    >
                      {savingTestimonial ? 'Guardando...' : editingTestimonial ? 'Actualizar' : 'Crear'}
                    </button>
                  </div>
                </div>
              )}

              {/* Testimonials List */}
              <div className="space-y-4">
                {testimonials.length === 0 ? (
                  <div className="bg-[#141414] rounded-xl p-12 text-center">
                    <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No hay reseñas. Crea la primera.</p>
                  </div>
                ) : (
                  testimonials.map(t => (
                    <div key={t.id} className={`bg-[#141414] rounded-xl p-6 ${!t.is_active ? 'opacity-50' : ''}`}>
                      <div className="flex items-start gap-4">
                        {t.person_image ? (
                          <img src={t.person_image} alt={t.person_name} className="w-16 h-16 rounded-full object-cover border-2 border-[#e4002b]" />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-[#e4002b]/20 flex items-center justify-center">
                            <Users className="w-8 h-8 text-[#e4002b]" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="text-white font-medium">{t.person_name}</p>
                              <p className="text-[#e4002b] text-sm">{t.position}</p>
                              <p className="text-white/50 text-sm">{t.cinema_name}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-white/30 text-xs">#{t.order}</span>
                              {!t.is_active && <span className="px-2 py-0.5 bg-white/10 text-white/50 text-xs rounded">Inactivo</span>}
                            </div>
                          </div>
                          <p className="text-white/70 text-sm mt-3 italic">"{t.quote}"</p>
                          <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                            <button 
                              onClick={() => {
                                setEditingTestimonial(t);
                                setShowTestimonialForm(true);
                                setTestimonialForm({
                                  person_name: t.person_name,
                                  person_image: t.person_image || '',
                                  position: t.position,
                                  cinema_name: t.cinema_name,
                                  quote: t.quote,
                                  order: t.order,
                                  is_active: t.is_active
                                });
                              }}
                              className="px-3 py-1.5 bg-white/10 text-white/80 rounded text-sm hover:bg-white/20"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => deleteTestimonial(t.id)}
                              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Podcasts Section */}
          {activeSection === 'podcasts' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-white text-xl font-semibold">Gestionar Podcasts</h2>
                  <p className="text-white/50 text-sm">Administra los podcasts que se muestran en la home</p>
                </div>
                <button
                  onClick={() => {
                    setEditingPodcast(null);
                    setShowPodcastForm(true);
                    setPodcastForm({ title: '', subtitle: '', image_url: '', spotify_url: '', is_enabled: true, is_locked: false, order: podcasts.length });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024]"
                  data-testid="add-podcast-btn"
                >
                  <Plus className="w-4 h-4" /> Nuevo Podcast
                </button>
              </div>

              {/* Podcast Form */}
              {showPodcastForm && (
                <div className="bg-[#141414] rounded-xl p-6 mb-6 border border-white/10">
                  <h3 className="text-white font-semibold mb-4">
                    {editingPodcast ? 'Editar Podcast' : 'Nuevo Podcast'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/70 text-sm mb-1">Título *</label>
                      <input
                        type="text"
                        value={podcastForm.title}
                        onChange={(e) => setPodcastForm({ ...podcastForm, title: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        placeholder="La Traviata | Podcast Ópera"
                        data-testid="podcast-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-1">Subtítulo</label>
                      <input
                        type="text"
                        value={podcastForm.subtitle}
                        onChange={(e) => setPodcastForm({ ...podcastForm, subtitle: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        placeholder="Episodio 1 - Introducción"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-1">URL de Imagen</label>
                      <input
                        type="text"
                        value={podcastForm.image_url}
                        onChange={(e) => setPodcastForm({ ...podcastForm, image_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        placeholder="https://..."
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-1">URL de Spotify *</label>
                      <input
                        type="text"
                        value={podcastForm.spotify_url}
                        onChange={(e) => setPodcastForm({ ...podcastForm, spotify_url: e.target.value })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        placeholder="https://open.spotify.com/episode/..."
                        data-testid="podcast-spotify-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-1">Orden</label>
                      <input
                        type="number"
                        value={podcastForm.order}
                        onChange={(e) => setPodcastForm({ ...podcastForm, order: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white"
                        min="0"
                      />
                    </div>
                    <div className="flex items-center gap-6 pt-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={podcastForm.is_enabled}
                          onChange={(e) => setPodcastForm({ ...podcastForm, is_enabled: e.target.checked })}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#e4002b]"
                        />
                        <span className="text-white/70 text-sm">Habilitado</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={podcastForm.is_locked}
                          onChange={(e) => setPodcastForm({ ...podcastForm, is_locked: e.target.checked })}
                          className="w-4 h-4 rounded border-white/20 bg-white/5 text-[#e4002b]"
                        />
                        <span className="text-white/70 text-sm flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Bloqueado (próximamente)
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={savePodcast}
                      disabled={savingPodcast || !podcastForm.title || !podcastForm.spotify_url}
                      className="flex items-center gap-2 px-6 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] disabled:opacity-50"
                      data-testid="save-podcast-btn"
                    >
                      {savingPodcast ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar
                    </button>
                    <button
                      onClick={() => {
                        setEditingPodcast(null);
                        setShowPodcastForm(false);
                        setPodcastForm({ title: '', subtitle: '', image_url: '', spotify_url: '', is_enabled: true, is_locked: false, order: 0 });
                      }}
                      className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Podcasts Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {podcasts.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <Mic className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No hay podcasts configurados</p>
                    <p className="text-white/30 text-sm">Añade tu primer podcast para mostrarlo en la home</p>
                  </div>
                ) : (
                  podcasts.map((podcast, index) => (
                    <div key={podcast.id} className="bg-[#141414] rounded-xl overflow-hidden border border-white/10 group">
                      {/* Podcast Image */}
                      <div className="aspect-square relative bg-cover bg-center" style={{ backgroundImage: podcast.image_url ? `url(${podcast.image_url})` : 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)' }}>
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                        {/* Badges */}
                        <div className="absolute top-3 left-3 flex gap-2">
                          {!podcast.is_enabled && (
                            <span className="px-2 py-1 bg-white/20 text-white text-xs rounded">Deshabilitado</span>
                          )}
                          {podcast.is_locked && (
                            <span className="px-2 py-1 bg-yellow-500/30 text-yellow-400 text-xs rounded flex items-center gap-1">
                              <Lock className="w-3 h-3" /> Bloqueado
                            </span>
                          )}
                        </div>
                        {/* Order badge */}
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-1 bg-black/50 text-white/70 text-xs rounded">#{podcast.order}</span>
                        </div>
                        {/* Play button overlay */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <a 
                            href={podcast.spotify_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-16 h-16 rounded-full bg-[#1DB954] flex items-center justify-center hover:scale-110 transition-transform"
                          >
                            <Play className="w-8 h-8 text-white ml-1" />
                          </a>
                        </div>
                        {/* Podcast icon if no image */}
                        {!podcast.image_url && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Mic className="w-16 h-16 text-white/20" />
                          </div>
                        )}
                      </div>
                      {/* Podcast Info */}
                      <div className="p-4">
                        <h3 className="text-white font-semibold truncate">{podcast.title}</h3>
                        {podcast.subtitle && (
                          <p className="text-white/50 text-sm truncate mt-1">{podcast.subtitle}</p>
                        )}
                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                          <button
                            onClick={() => togglePodcastEnabled(podcast)}
                            className={`p-2 rounded ${podcast.is_enabled ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'} hover:opacity-80`}
                            title={podcast.is_enabled ? 'Deshabilitar' : 'Habilitar'}
                          >
                            {podcast.is_enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => togglePodcastLocked(podcast)}
                            className={`p-2 rounded ${podcast.is_locked ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/50'} hover:opacity-80`}
                            title={podcast.is_locked ? 'Desbloquear' : 'Bloquear'}
                          >
                            {podcast.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => {
                              setEditingPodcast(podcast);
                              setShowPodcastForm(true);
                              setPodcastForm({
                                title: podcast.title,
                                subtitle: podcast.subtitle || '',
                                image_url: podcast.image_url || '',
                                spotify_url: podcast.spotify_url,
                                is_enabled: podcast.is_enabled,
                                is_locked: podcast.is_locked,
                                order: podcast.order
                              });
                            }}
                            className="p-2 bg-white/10 text-white/70 rounded hover:bg-white/20"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deletePodcast(podcast.id)}
                            className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <a
                            href={podcast.spotify_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-[#1DB954]/20 text-[#1DB954] rounded hover:bg-[#1DB954]/30 ml-auto"
                            title="Abrir en Spotify"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeSection === 'content' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-white text-xl font-semibold">Gestionar Contenidos</h2>
                  <p className="text-white/50 text-sm">{contents.length > 0 ? contents.length : contentDatabase.length} contenidos</p>
                </div>
                <button
                  onClick={() => openContentEditor(null)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024]"
                >
                  <Plus className="w-4 h-4" /> Nuevo Contenido
                </button>
              </div>

              {/* Content Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {(contents.length > 0 ? contents : contentDatabase).map(content => (
                  <div key={content.id} className="bg-[#141414] rounded-xl overflow-hidden group">
                    <div className="aspect-video relative bg-cover bg-center" style={{ backgroundImage: `url(${content.image})` }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                      {/* Top left badges for Live/Encore */}
                      <div className="absolute top-2 left-2 flex gap-2">
                        {content.is_live && (
                          <span className="px-2 py-1 bg-[#e4002b] text-white text-xs rounded font-bold">DIRECTO</span>
                        )}
                        {content.is_encore && (
                          <span className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-bold">ENCORE</span>
                        )}
                      </div>
                      <div className="absolute top-2 right-2 flex gap-2">
                        <span className="px-2 py-1 bg-black/50 text-white/80 text-xs rounded">{content.category}</span>
                        {contentRestrictions[content.id] && (
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded flex items-center gap-1">
                            <Lock className="w-3 h-3" /> Restringido
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-0 p-4">
                        <h3 className="text-white font-semibold">{content.title}</h3>
                        <p className="text-[#e4002b] text-sm">{content.composer}</p>
                        {content.theater && <p className="text-white/50 text-xs">{content.theater}</p>}
                        {/* Show encore dates if applicable */}
                        {content.is_encore && content.encore_start_date && content.encore_end_date && (
                          <p className="text-amber-400 text-xs mt-1">
                            Disponible: {content.encore_start_date} - {content.encore_end_date}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openContentEditor(content)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white/10 text-white rounded text-sm hover:bg-white/20"
                        >
                          <Edit2 className="w-3 h-3" /> Editar
                        </button>
                        <button
                          onClick={() => duplicateContent(content.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded text-sm hover:bg-amber-500/30"
                          title="Duplicar contenido (útil para crear versión Encore)"
                        >
                          <Copy className="w-3 h-3" /> Duplicar
                        </button>
                        <button
                          onClick={() => toggleContentRestriction(content.id)}
                          className={`px-3 py-1.5 rounded text-sm ${
                            contentRestrictions[content.id] ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/10 text-white/60'
                          }`}
                        >
                          {contentRestrictions[content.id] ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                        </button>
                      </div>
                      <button
                        onClick={() => deleteContent(content.id)}
                        className="p-1.5 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Editor Modal */}
          {showContentEditor && (
            <ContentEditor
              content={selectedContentForEdit}
              onClose={() => { setShowContentEditor(false); setSelectedContentForEdit(null); }}
              onSave={loadContents}
              sections={homeSections}
            />
          )}

          {activeSection === 'sections' && (
            <div>
              <div className="mb-6">
                <h2 className="text-white text-xl font-semibold mb-2">Gestionar Secciones de la Home</h2>
                <p className="text-white/50 text-sm">Añade, edita o elimina las secciones que aparecen en la página principal</p>
              </div>

              {/* Add new section */}
              <div className="bg-[#141414] rounded-xl p-6 mb-6">
                <h3 className="text-white font-semibold mb-4">Añadir nueva sección</h3>
                <div className="flex flex-wrap gap-4">
                  <input
                    type="text"
                    placeholder="Nombre de la sección"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    className="flex-1 min-w-[200px] px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:border-[#e4002b] focus:outline-none"
                  />
                  <select
                    value={newSectionType}
                    onChange={(e) => setNewSectionType(e.target.value)}
                    className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                  >
                    <option value="content_carousel">Carrusel de contenidos</option>
                    <option value="detail_carousel">Carrusel detallado</option>
                    <option value="concert_carousel">Carrusel de conciertos</option>
                  </select>
                  <button
                    onClick={createSection}
                    disabled={!newSectionName.trim()}
                    className="flex items-center gap-2 px-6 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" /> Añadir
                  </button>
                </div>
              </div>

              {/* Sections list */}
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <p className="text-white/50 text-sm">{homeSections.length} secciones configuradas</p>
                </div>
                <div className="divide-y divide-white/5">
                  {homeSections.map((section, idx) => (
                    <div key={section.id} className="bg-[#141414]">
                      <div className="flex items-center gap-4 p-4 hover:bg-white/5">
                        {/* Order controls */}
                        <div className="flex flex-col gap-1">
                          <button 
                            onClick={() => moveSection(section.id, 'up')}
                            disabled={idx === 0}
                            className="p-1 text-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <GripVertical className="w-4 h-4 rotate-180" />
                          </button>
                          <button 
                            onClick={() => moveSection(section.id, 'down')}
                            disabled={idx === homeSections.length - 1}
                            className="p-1 text-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <GripVertical className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Order number */}
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white font-bold">
                          {idx + 1}
                        </div>

                        {/* Section info */}
                        <div className="flex-1">
                          {editingSection === section.id ? (
                            <input
                              type="text"
                              defaultValue={section.name}
                              autoFocus
                              onBlur={(e) => updateSection(section.id, { name: e.target.value })}
                              onKeyDown={(e) => e.key === 'Enter' && updateSection(section.id, { name: e.target.value })}
                              className="w-full px-3 py-1 bg-white/10 border border-[#e4002b] rounded text-white focus:outline-none"
                            />
                          ) : (
                            <h4 className="text-white font-medium">{section.name}</h4>
                          )}
                          <p className="text-white/50 text-sm">
                            {sectionTypeLabels[section.type] || section.type} · {(section.contents || []).length} contenidos
                          </p>
                        </div>

                        {/* Content count badge */}
                        <span className="px-2 py-1 bg-[#e4002b]/20 text-[#e4002b] rounded text-xs font-medium">
                          {(section.contents || []).filter(c => c.is_visible).length} visibles
                        </span>

                        {/* Status toggle */}
                        <button
                          onClick={() => updateSection(section.id, { is_active: !section.is_active })}
                          className={`px-3 py-1 rounded text-sm font-medium ${
                            section.is_active 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-white/10 text-white/40'
                          }`}
                        >
                          {section.is_active ? 'Activa' : 'Inactiva'}
                        </button>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleExpandSection(section.id)}
                            className={`p-2 rounded transition-colors ${expandedSection === section.id ? 'bg-[#e4002b] text-white' : 'text-white/50 hover:text-white hover:bg-white/10'}`}
                            title="Gestionar contenidos"
                          >
                            <ChevronDown className={`w-4 h-4 transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`} />
                          </button>
                          <button
                            onClick={() => setEditingSection(editingSection === section.id ? null : section.id)}
                            className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded"
                          >
                            {editingSection === section.id ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => deleteSection(section.id)}
                            className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Expanded content management */}
                      {expandedSection === section.id && (
                        <div className="bg-[#0f0f0f] border-t border-white/10 p-4">
                          {/* Add content with search */}
                          <div className="mb-4 relative">
                            <label className="block text-white/60 text-sm mb-2">Añadir contenido a esta sección</label>
                            <div className="flex gap-2">
                              <div className="flex-1 relative">
                                <input
                                  type="text"
                                  value={contentSearchQuery}
                                  onChange={(e) => {
                                    setContentSearchQuery(e.target.value);
                                    setShowContentDropdown(section.id);
                                  }}
                                  onFocus={() => setShowContentDropdown(section.id)}
                                  placeholder="Buscar contenido por título o compositor..."
                                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none"
                                />
                                {/* Search Results Dropdown */}
                                {showContentDropdown === section.id && (
                                  <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50">
                                    {getAvailableContentsForSection(section.id).length === 0 ? (
                                      <p className="px-4 py-3 text-white/40 text-sm">No hay contenidos disponibles</p>
                                    ) : (
                                      getAvailableContentsForSection(section.id).slice(0, 10).map(c => (
                                        <button
                                          key={c.id}
                                          onClick={() => {
                                            addContentToSection(section.id, c.id);
                                            setContentSearchQuery('');
                                            setShowContentDropdown(null);
                                          }}
                                          className="w-full px-4 py-3 text-left hover:bg-white/10 flex items-center gap-3 border-b border-white/5 last:border-0"
                                        >
                                          {c.image && (
                                            <img src={c.hero_image || c.image} alt="" className="w-10 h-10 object-cover rounded" />
                                          )}
                                          <div>
                                            <p className="text-white font-medium">{c.title}</p>
                                            <p className="text-white/50 text-sm">{c.composer}</p>
                                          </div>
                                        </button>
                                      ))
                                    )}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => setShowContentDropdown(showContentDropdown === section.id ? null : section.id)}
                                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20"
                              >
                                <ChevronDown className={`w-4 h-4 transition-transform ${showContentDropdown === section.id ? 'rotate-180' : ''}`} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Contents list */}
                          <div className="space-y-2">
                            <p className="text-white/40 text-xs uppercase tracking-wide mb-2">Contenidos en esta sección (arrastra para reordenar)</p>
                            {(sectionContents[section.id] || []).length === 0 ? (
                              <p className="text-white/30 text-sm py-4 text-center">No hay contenidos en esta sección</p>
                            ) : (
                              (sectionContents[section.id] || []).map((content, contentIdx) => (
                                <div key={content.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg group hover:bg-white/10 transition-colors">
                                  {/* Order controls */}
                                  <div className="flex flex-col gap-0.5">
                                    <button
                                      onClick={() => moveContentInSection(section.id, contentIdx, 'up')}
                                      disabled={contentIdx === 0}
                                      className="p-1 text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                                      title="Mover arriba"
                                    >
                                      <GripVertical className="w-3 h-3 rotate-180" />
                                    </button>
                                    <button
                                      onClick={() => moveContentInSection(section.id, contentIdx, 'down')}
                                      disabled={contentIdx === (sectionContents[section.id] || []).length - 1}
                                      className="p-1 text-white/30 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                                      title="Mover abajo"
                                    >
                                      <GripVertical className="w-3 h-3" />
                                    </button>
                                  </div>
                                  
                                  {/* Order number */}
                                  <span className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-white/50 text-xs font-bold">
                                    {contentIdx + 1}
                                  </span>
                                  
                                  <img 
                                    src={content.image} 
                                    alt={content.title} 
                                    className="w-16 h-10 object-cover rounded"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium truncate">{content.title}</p>
                                    <p className="text-white/50 text-sm truncate">{content.composer}</p>
                                  </div>
                                  
                                  {/* Type badges */}
                                  {content.is_live && (
                                    <span className="px-2 py-0.5 bg-[#e4002b]/20 text-[#e4002b] text-xs rounded">DIRECTO</span>
                                  )}
                                  {content.is_encore && (
                                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">ENCORE</span>
                                  )}
                                  
                                  <button
                                    onClick={() => toggleContentVisibility(section.id, content.id)}
                                    className={`p-2 rounded transition-colors ${
                                      content.is_visible_in_section 
                                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                                        : 'bg-white/10 text-white/40 hover:bg-white/20'
                                    }`}
                                    title={content.is_visible_in_section ? 'Visible - Clic para ocultar' : 'Oculto - Clic para mostrar'}
                                  >
                                    {content.is_visible_in_section ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                  </button>
                                  <button
                                    onClick={() => removeContentFromSection(section.id, content.id)}
                                    className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                                    title="Quitar de la sección"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Navigation Menu Management Section */}
          {activeSection === 'navigation' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-white text-xl font-semibold">Gestionar Menú de Navegación</h2>
                  <p className="text-white/50 text-sm">Configura los menús y submenús que aparecen en la cabecera de la web pública</p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${BACKEND_URL}/api/navigation-menus`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ label: 'NUEVO MENÚ', icon: 'Info', order: navMenus.length, is_active: false, has_dropdown: true, items: [] })
                      });
                      if (res.ok) { loadNavMenus(); toast.success('Menú creado'); }
                    } catch (err) { console.error(err); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#e4002b] text-white text-sm rounded-lg hover:bg-[#c70024] transition-colors"
                  data-testid="add-menu-btn"
                >
                  <Plus className="w-4 h-4" /> Añadir menú
                </button>
              </div>

              <div className="space-y-4">
                {navMenus.map((menu) => (
                  <div key={menu.id} className="bg-[#141414] rounded-xl p-6 border border-white/10">
                    {/* Menu Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={async () => {
                            await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ is_active: !menu.is_active })
                            });
                            loadNavMenus();
                          }}
                          className={`px-3 py-1 rounded text-xs font-medium ${menu.is_active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                        >
                          {menu.is_active ? 'Activo' : 'Inactivo'}
                        </button>
                        {editingMenu === menu.id ? (
                          <input
                            type="text"
                            defaultValue={menu.label}
                            onBlur={async (e) => {
                              await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ label: e.target.value })
                              });
                              loadNavMenus();
                            }}
                            className="px-3 py-1 bg-white/10 border border-white/20 rounded text-white font-bold"
                            autoFocus
                          />
                        ) : (
                          <h3 className="text-white font-bold text-lg">{menu.label}</h3>
                        )}
                        <span className="text-white/40 text-xs">
                          {menu.has_dropdown ? '(Desplegable)' : '(Enlace directo)'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={menu.icon || 'Info'}
                          onChange={async (e) => {
                            await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ icon: e.target.value })
                            });
                            loadNavMenus();
                          }}
                          className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs"
                        >
                          <option value="Info">Info</option>
                          <option value="Film">Film</option>
                          <option value="Play">Play</option>
                          <option value="Tv">Tv</option>
                          <option value="Calendar">Calendar</option>
                          <option value="BookOpen">BookOpen</option>
                          <option value="Users">Users</option>
                          <option value="Globe">Globe</option>
                        </select>
                        <label className="flex items-center gap-1 text-white/60 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={menu.has_dropdown}
                            onChange={async (e) => {
                              await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ has_dropdown: e.target.checked })
                              });
                              loadNavMenus();
                            }}
                            className="w-3 h-3 accent-[#e4002b]"
                          />
                          Desplegable
                        </label>
                        <button
                          onClick={() => setEditingMenu(editingMenu === menu.id ? null : menu.id)}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`¿Eliminar el menú "${menu.label}"?`)) return;
                            await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, { method: 'DELETE' });
                            loadNavMenus();
                            toast.success('Menú eliminado');
                          }}
                          className="p-2 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Direct link path (when not dropdown) */}
                    {!menu.has_dropdown && (
                      <div className="mb-4">
                        <label className="block text-white/50 text-xs mb-1">Ruta del enlace</label>
                        <input
                          type="text"
                          defaultValue={menu.path || ''}
                          onBlur={async (e) => {
                            await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ path: e.target.value })
                            });
                            loadNavMenus();
                          }}
                          placeholder="/ruta-destino"
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm"
                        />
                      </div>
                    )}

                    {/* Submenu Items */}
                    {menu.has_dropdown && (
                      <div className="space-y-2">
                        <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Submenús ({(menu.items || []).length})</p>
                        {(menu.items || []).map((item, idx) => (
                          <div key={item.id || idx} className="flex items-center gap-3 bg-white/5 rounded-lg p-3">
                            <span className="text-white/30 text-xs w-6">{idx + 1}</span>
                            <input
                              type="text"
                              defaultValue={item.label}
                              onBlur={async (e) => {
                                const newItems = [...(menu.items || [])];
                                newItems[idx] = { ...newItems[idx], label: e.target.value };
                                await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ items: newItems })
                                });
                                loadNavMenus();
                              }}
                              className="flex-1 px-3 py-1.5 bg-white/10 border border-white/10 rounded text-white text-sm"
                              placeholder="Nombre del enlace"
                            />
                            <input
                              type="text"
                              defaultValue={item.path}
                              onBlur={async (e) => {
                                const newItems = [...(menu.items || [])];
                                newItems[idx] = { ...newItems[idx], path: e.target.value };
                                await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ items: newItems })
                                });
                                loadNavMenus();
                              }}
                              className="w-48 px-3 py-1.5 bg-white/10 border border-white/10 rounded text-white/70 text-sm font-mono"
                              placeholder="/ruta"
                            />
                            <button
                              onClick={async () => {
                                const newItems = [...(menu.items || [])];
                                newItems[idx] = { ...newItems[idx], is_active: !item.is_active };
                                await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ items: newItems })
                                });
                                loadNavMenus();
                              }}
                              className={`px-2 py-1 rounded text-xs ${item.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                            >
                              {item.is_active !== false ? 'Activo' : 'Oculto'}
                            </button>
                            <button
                              onClick={async () => {
                                const newItems = (menu.items || []).filter((_, i) => i !== idx);
                                await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ items: newItems })
                                });
                                loadNavMenus();
                                toast.success('Submenú eliminado');
                              }}
                              className="p-1.5 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                        {/* Add new submenu item */}
                        <button
                          onClick={async () => {
                            const newItems = [...(menu.items || []), {
                              id: String(Date.now()),
                              label: 'Nuevo enlace',
                              path: '/',
                              order: (menu.items || []).length,
                              is_active: true
                            }];
                            await fetch(`${BACKEND_URL}/api/navigation-menus/${menu.id}`, {
                              method: 'PUT', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ items: newItems })
                            });
                            loadNavMenus();
                          }}
                          className="w-full py-2 border border-dashed border-white/20 text-white/50 hover:text-white hover:border-white/40 rounded-lg text-xs flex items-center justify-center gap-1 transition-colors"
                        >
                          <Plus className="w-3 h-3" /> Añadir submenú
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {navMenus.length === 0 && (
                <div className="bg-[#141414] rounded-xl p-8 text-center">
                  <Globe className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50 mb-4">No hay menús configurados</p>
                  <button
                    onClick={async () => {
                      await fetch(`${BACKEND_URL}/api/navigation-menus/seed`, { method: 'POST' });
                      loadNavMenus();
                      toast.success('Menús por defecto creados');
                    }}
                    className="px-4 py-2 bg-[#e4002b] text-white text-sm rounded-lg hover:bg-[#c70024]"
                  >
                    Cargar menús por defecto
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Bravo Network Management Section */}
          {activeSection === 'bravo-network' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-white text-xl font-semibold">Red de Cines Bravo</h2>
                  <p className="text-white/50 text-sm">Gestiona el listado de cines que trabajan con Bravo (aparece en /colabora)</p>
                </div>
                <div className="flex gap-2">
                  <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Importar Excel
                    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setBravoUploading(true);
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/bravo-cinemas/upload-excel`, { method: 'POST', body: fd });
                        const data = await res.json();
                        if (res.ok) { toast.success(`${data.count} cines importados`); loadBravoCinemas(); }
                        else toast.error(data.detail || 'Error al importar');
                      } catch { toast.error('Error de conexión'); }
                      finally { setBravoUploading(false); e.target.value = ''; }
                    }} />
                  </label>
                </div>
              </div>

              {/* Add cinema form */}
              <div className="bg-[#141414] rounded-xl p-5 mb-6 border border-white/10">
                <h3 className="text-white font-semibold mb-4 text-sm">Añadir cine manualmente</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <input type="text" value={bravoForm.nombre} onChange={e => setBravoForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre del cine *" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30" />
                  <input type="text" value={bravoForm.circuito} onChange={e => setBravoForm(f => ({ ...f, circuito: e.target.value }))} placeholder="Circuito" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30" />
                  <input type="text" value={bravoForm.poblacion} onChange={e => setBravoForm(f => ({ ...f, poblacion: e.target.value }))} placeholder="Población" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30" />
                  <input type="text" value={bravoForm.provincia} onChange={e => setBravoForm(f => ({ ...f, provincia: e.target.value }))} placeholder="Provincia" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30" />
                  <input type="text" value={bravoForm.comunidad_autonoma} onChange={e => setBravoForm(f => ({ ...f, comunidad_autonoma: e.target.value }))} placeholder="Comunidad Autónoma" className="px-3 py-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30" />
                </div>
                <button
                  onClick={async () => {
                    if (!bravoForm.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
                    try {
                      const res = await fetch(`${BACKEND_URL}/api/bravo-cinemas`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(bravoForm)
                      });
                      if (res.ok) { toast.success('Cine añadido'); loadBravoCinemas(); setBravoForm({ nombre: '', circuito: '', poblacion: '', provincia: '', comunidad_autonoma: '' }); }
                    } catch { toast.error('Error al añadir'); }
                  }}
                  className="mt-3 px-4 py-2 bg-[#e4002b] text-white text-sm rounded-lg hover:bg-[#c70024] flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Añadir cine
                </button>
                <p className="text-white/30 text-xs mt-3">Para importar por Excel: columnas en orden → Nombre, Circuito, Población, Provincia, Comunidad Autónoma (primera fila = cabeceras)</p>
              </div>

              {/* Cinema list */}
              <div className="bg-[#141414] rounded-xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-white/5 border-b border-white/10">
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">Cine</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">Circuito</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">Población</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">Provincia</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">C. Autónoma</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-white/40">Estado</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-white/40">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bravoCinemas.map((bc) => (
                        <tr key={bc.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="px-4 py-3 text-sm text-white font-medium">{bc.nombre}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{bc.circuito}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{bc.poblacion}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{bc.provincia}</td>
                          <td className="px-4 py-3 text-sm text-white/60">{bc.comunidad_autonoma}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={async () => {
                                await fetch(`${BACKEND_URL}/api/bravo-cinemas/${bc.id}`, {
                                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ is_active: !bc.is_active })
                                });
                                loadBravoCinemas();
                              }}
                              className={`px-2 py-1 rounded text-xs ${bc.is_active ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/40'}`}
                            >
                              {bc.is_active ? 'Visible' : 'Oculto'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={async () => {
                                if (!window.confirm(`¿Eliminar "${bc.nombre}"?`)) return;
                                await fetch(`${BACKEND_URL}/api/bravo-cinemas/${bc.id}`, { method: 'DELETE' });
                                loadBravoCinemas();
                                toast.success('Cine eliminado');
                              }}
                              className="p-1.5 text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 bg-white/5 border-t border-white/10 text-right">
                  <span className="text-white/30 text-sm">{bravoCinemas.length} cines en total · {bravoCinemas.filter(c => c.is_active).length} visibles</span>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'cinemas' && (
            <div>
              <div className="flex gap-2 mb-6 flex-wrap">
                {['all', 'pending', 'approved', 'inactive', 'rejected', 'deleted'].map(status => (
                  <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 rounded-lg text-sm font-medium ${filterStatus === status ? (status === 'deleted' ? 'bg-red-600 text-white' : 'bg-[#e4002b] text-white') : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                    {status === 'all' ? 'Todos' : status === 'deleted' ? 'Papelera' : statusLabels[status]}
                    {status === 'deleted' && <Trash2 className="w-3 h-3 ml-1 inline" />}
                  </button>
                ))}
              </div>
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead><tr className="border-b border-white/10"><th className="text-left text-white/50 text-xs font-medium px-6 py-4">BRAVO</th><th className="text-left text-white/50 text-xs font-medium px-6 py-4">RECINTO</th><th className="text-left text-white/50 text-xs font-medium px-6 py-4">EMPRESA</th><th className="text-left text-white/50 text-xs font-medium px-6 py-4">CONTACTO</th><th className="text-left text-white/50 text-xs font-medium px-6 py-4">ESTADO</th><th className="text-right text-white/50 text-xs font-medium px-6 py-4">ACCIONES</th></tr></thead>
                  <tbody>
                    {cinemas
                      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                      .filter(c => {
                      // For multi-venue cinemas, check if at least one venue matches the filter
                      if (c.venues && c.venues.length > 0) {
                        if (filterStatus === 'all') return c.status !== 'deleted';
                        if (filterStatus === 'deleted') return c.status === 'deleted';
                        return c.venues.some(v => (v.status || 'pending') === filterStatus);
                      }
                      // For single-venue cinemas, use the cinema status
                      if (filterStatus === 'all') return c.status !== 'deleted';
                      return c.status === filterStatus;
                    }).flatMap(c => {
                      // If cinema has multiple venues, create a row for each venue
                      const hasMultipleVenues = c.venues && c.venues.length > 0;
                      const venues = hasMultipleVenues ? c.venues : [{ venue_name: c.venue_name, status: c.status, bravo_number: c.bravo_number }];
                      return venues
                        .filter(venue => filterStatus === 'all' || filterStatus === 'deleted' || (venue.status || 'pending') === filterStatus)
                        .map((venue, venueIndex) => {
                        // Get the actual venue index in the original array (for multi-venue)
                        const actualVenueIndex = hasMultipleVenues ? c.venues.findIndex(v => v === venue) : 0;
                        const venueStatus = c.status === 'deleted' ? 'deleted' : (venue.status || 'pending');
                        const venueBravo = venue.bravo_number || (hasMultipleVenues ? null : c.bravo_number);
                        return (
                        <tr key={`${c.id}-${actualVenueIndex}`} className={`border-b border-white/5 hover:bg-white/5 ${c.status === 'deleted' ? 'opacity-60' : ''}`}>
                          <td className="px-6 py-4">
                            {venueStatus === 'approved' ? (
                              venueBravo ? (
                                <span className="px-3 py-1.5 bg-[#e4002b] text-white font-bold rounded text-sm tracking-wider">{venueBravo}</span>
                              ) : (
                                <button 
                                  onClick={() => { setSelectedCinema({...c, selectedVenueIndex: actualVenueIndex}); setShowBravoModal(true); setBravoNumber(''); }}
                                  className="px-3 py-1.5 border-2 border-dashed border-[#e4002b]/50 text-[#e4002b]/70 rounded text-xs hover:border-[#e4002b] hover:text-[#e4002b] flex items-center gap-1"
                                >
                                  <Hash className="w-3 h-3" />
                                  Asignar
                                </button>
                              )
                            ) : (
                              <span className="text-white/30 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-white font-medium">{venue.venue_name || c.venue_name}</p>
                            {venue.address?.city && <p className="text-white/50 text-sm">{venue.address.city}</p>}
                            {hasMultipleVenues && <span className="text-xs text-[#e4002b] bg-[#e4002b]/10 px-2 py-0.5 rounded mt-1 inline-block">Recinto {actualVenueIndex + 1} de {c.venues.length}</span>}
                          </td>
                          <td className="px-6 py-4"><p className="text-white/70">{c.company_name}</p></td>
                          <td className="px-6 py-4"><p className="text-white/70">{c.contact_name}</p><p className="text-white/50 text-sm">{c.contact_email}</p></td>
                          <td className="px-6 py-4"><span className={`px-2 py-1 rounded border text-xs font-medium ${statusColors[venueStatus]}`}>{statusLabels[venueStatus]}</span></td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex gap-2 justify-end">
                              {/* Ver Ficha button - cada recinto tiene su ficha */}
                              <button 
                                onClick={() => { setSelectedCinema({...c, selectedVenueIndex: actualVenueIndex}); setShowCinemaDetail(true); }} 
                                className="flex items-center gap-1 px-3 py-1.5 bg-white/10 text-white/80 rounded hover:bg-white/20 text-sm"
                                title="Ver ficha del recinto"
                              >
                                <FileText className="w-4 h-4" />
                                Ver ficha
                              </button>
                              {c.status === 'deleted' ? (
                                <>
                                  <button 
                                    onClick={() => handleRestore('cinema', c.id)} 
                                    className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30" 
                                    title="Restaurar"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => setDeleteConfirmModal({ type: 'cinema', id: c.id, name: venue.venue_name || c.company_name, permanent: true })} 
                                    className="p-1.5 bg-red-600/30 text-red-400 rounded hover:bg-red-600/50" 
                                    title="Eliminar permanentemente"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  {venueStatus === 'pending' && (
                                    <>
                                      <button onClick={() => updateCinemaStatus(c.id, 'approved', hasMultipleVenues ? actualVenueIndex : null)} className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30" title="Aprobar"><Check className="w-4 h-4" /></button>
                                      <button onClick={() => updateCinemaStatus(c.id, 'rejected', hasMultipleVenues ? actualVenueIndex : null)} className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" title="Rechazar"><X className="w-4 h-4" /></button>
                                    </>
                                  )}
                                  {venueStatus === 'approved' && (
                                    <button onClick={() => deactivateCinema(c.id)} className="p-1.5 bg-gray-500/20 text-gray-400 rounded hover:bg-gray-500/30" title="Inactivar cine"><EyeOff className="w-4 h-4" /></button>
                                  )}
                                  {venueStatus === 'inactive' && (
                                    <button onClick={() => reactivateCinema(c.id)} className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30" title="Reactivar cine"><Eye className="w-4 h-4" /></button>
                                  )}
                                  <button 
                                    onClick={() => setDeleteConfirmModal({ type: 'cinema', id: c.id, name: venue.venue_name || c.company_name, permanent: false })} 
                                    className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30" 
                                    title="Mover a papelera"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                    })}
                  </tbody>
                </table>
                {cinemas.filter(c => filterStatus === 'all' ? c.status !== 'deleted' : c.status === filterStatus).length === 0 && (
                  <div className="text-center py-12"><Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" /><p className="text-white/50">{filterStatus === 'deleted' ? 'No hay cines en la papelera' : 'No hay cines'}</p></div>
                )}
              </div>
            </div>
          )}

          {/* Cinema Detail Modal */}
          {showCinemaDetail && selectedCinema && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCinemaDetail(false)}>
              <div className="bg-[#1a1a1a] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-[#1a1a1a] border-b border-white/10 p-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-white text-xl font-bold">Ficha del Recinto</h2>
                    <p className="text-white/50 text-sm">
                      {selectedCinema.venues && selectedCinema.venues.length > 0 && selectedCinema.selectedVenueIndex !== undefined
                        ? selectedCinema.venues[selectedCinema.selectedVenueIndex]?.venue_name || `Recinto ${selectedCinema.selectedVenueIndex + 1}`
                        : selectedCinema.venue_name || 'Información del registro'
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {(() => {
                      // Get venue-specific status for multi-venue cinemas
                      const hasMultipleVenues = selectedCinema.venues && selectedCinema.venues.length > 0;
                      const venueIndex = selectedCinema.selectedVenueIndex;
                      const venueStatus = hasMultipleVenues && venueIndex !== undefined
                        ? (selectedCinema.venues[venueIndex]?.status || 'pending')
                        : selectedCinema.status;
                      return (
                        <span className={`px-3 py-1 rounded border text-sm font-medium ${statusColors[venueStatus]}`}>
                          {statusLabels[venueStatus]}
                        </span>
                      );
                    })()}
                    <button onClick={() => setShowCinemaDetail(false)} className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-3">
                  {/* Bravo Number - Always visible, not in accordion */}
                  {(() => {
                    // Determine the correct status and bravo_number based on whether it's multi-venue
                    const hasMultipleVenues = selectedCinema.venues && selectedCinema.venues.length > 0;
                    const venueIndex = selectedCinema.selectedVenueIndex;
                    const currentVenue = hasMultipleVenues && venueIndex !== undefined 
                      ? selectedCinema.venues[venueIndex] 
                      : null;
                    const currentStatus = currentVenue ? (currentVenue.status || 'pending') : selectedCinema.status;
                    const currentBravoNumber = currentVenue ? currentVenue.bravo_number : selectedCinema.bravo_number;
                    
                    return currentStatus === 'approved' && (
                      <div className={`rounded-xl p-4 ${currentBravoNumber ? 'bg-gradient-to-r from-[#e4002b] to-[#c70024]' : 'bg-[#141414] border-2 border-dashed border-[#e4002b]/50'}`}>
                        {currentBravoNumber ? (
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white/80 text-xs uppercase tracking-wider">Número de Bravo</p>
                              <p className="text-white text-3xl font-bold tracking-widest">{currentBravoNumber}</p>
                            </div>
                            {cinemaPricings[selectedCinema.id]?.bravo_rental && (
                              <div className="text-right">
                                <p className="text-white/70 text-xs uppercase tracking-wider">Modalidad</p>
                                <p className="text-white text-sm font-medium">
                                  {cinemaPricings[selectedCinema.id].bravo_rental.mode === 'por_evento' && 'Por Evento'}
                                  {cinemaPricings[selectedCinema.id].bravo_rental.mode === 'anual_3_years' && 'Anual Premium (2 años)'}
                                  {cinemaPricings[selectedCinema.id].bravo_rental.mode === 'anual_1_year' && 'Anual Flexible (1 año)'}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Hash className="w-8 h-8 text-[#e4002b]/50" />
                              <p className="text-white/60">Sin número de Bravo asignado</p>
                            </div>
                            <button 
                              onClick={() => { setShowBravoModal(true); setBravoNumber(''); }}
                              className="px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] text-sm font-medium"
                            >
                              Asignar
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Pending Profile Changes */}
                  {selectedCinema.pending_changes_status === 'pending' && selectedCinema.pending_changes && (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Cambios de perfil pendientes de validación</p>
                          <p className="text-white/60 text-sm">El cine ha solicitado actualizar su información</p>
                        </div>
                      </div>
                      <div className="bg-black/20 rounded-lg p-4 mb-4 max-h-64 overflow-y-auto">
                        <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Cambios solicitados:</p>
                        {(() => {
                          try {
                            // Parse pending_changes - handle multiple levels of stringification
                            let changes = selectedCinema.pending_changes;
                            // Keep parsing while it's still a string (handles double-stringified JSON)
                            while (typeof changes === 'string') {
                              changes = JSON.parse(changes);
                            }
                            
                            // If no valid keys found, show raw data
                            const hasValidKeys = changes && typeof changes === 'object' && 
                              (changes.company_name || changes.cif || changes.venue_name || 
                               changes.contact_name || changes.contact_phone || changes.address || 
                               changes.technical_specs);
                            
                            if (!hasValidKeys) {
                              return (
                                <pre className="text-white/70 text-xs whitespace-pre-wrap">
                                  {JSON.stringify(changes, null, 2)}
                                </pre>
                              );
                            }
                            
                            return (
                              <div className="space-y-2 text-sm">
                              {changes.company_name && (
                                <div className="flex justify-between">
                                  <span className="text-white/50">Razón Social:</span>
                                  <span className="text-white">{changes.company_name}</span>
                                </div>
                              )}
                              {changes.cif && (
                                <div className="flex justify-between">
                                  <span className="text-white/50">CIF:</span>
                                  <span className="text-white">{changes.cif}</span>
                                </div>
                              )}
                              {changes.venue_name && (
                                <div className="flex justify-between">
                                  <span className="text-white/50">Nombre del recinto:</span>
                                  <span className="text-white">{changes.venue_name}</span>
                                </div>
                              )}
                              {changes.contact_name && (
                                <div className="flex justify-between">
                                  <span className="text-white/50">Contacto:</span>
                                  <span className="text-white">{changes.contact_name}</span>
                                </div>
                              )}
                              {changes.contact_phone && (
                                <div className="flex justify-between">
                                  <span className="text-white/50">Teléfono:</span>
                                  <span className="text-white">{changes.contact_phone}</span>
                                </div>
                              )}
                              {changes.address && (
                                <div className="pt-2 border-t border-white/10">
                                  <span className="text-white/50 block mb-1">Nueva dirección:</span>
                                  <span className="text-white text-xs">
                                    {[
                                      changes.address.street,
                                      changes.address.city,
                                      changes.address.postal_code,
                                      changes.address.province
                                    ].filter(Boolean).join(', ')}
                                  </span>
                                </div>
                              )}
                              {changes.technical_specs && (
                                <div className="pt-2 border-t border-white/10">
                                  <span className="text-white/50 block mb-1">Especificaciones técnicas:</span>
                                  <div className="grid grid-cols-2 gap-1 text-xs">
                                    {changes.technical_specs.projection_system && (
                                      <span className="text-white">Proyección: {changes.technical_specs.projection_system}</span>
                                    )}
                                    {changes.technical_specs.sound_system && (
                                      <span className="text-white">Sonido: {changes.technical_specs.sound_system}</span>
                                    )}
                                    {changes.technical_specs.seating_capacity && (
                                      <span className="text-white">Aforo: {changes.technical_specs.seating_capacity}</span>
                                    )}
                                    {changes.technical_specs.screen_size && (
                                      <span className="text-white">Pantalla: {changes.technical_specs.screen_size}</span>
                                    )}
                                    {changes.technical_specs.has_subtitles_capability !== undefined && (
                                      <span className="text-white">Subtítulos: {changes.technical_specs.has_subtitles_capability ? 'Sí' : 'No'}</span>
                                    )}
                                    {changes.technical_specs.has_accessibility !== undefined && (
                                      <span className="text-white">Accesibilidad: {changes.technical_specs.has_accessibility ? 'Sí' : 'No'}</span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                          } catch (parseError) {
                            // If parsing fails, show raw data in a readable format
                            console.error('Error parsing pending_changes:', parseError);
                            return (
                              <pre className="text-white/70 text-xs whitespace-pre-wrap">
                                {typeof selectedCinema.pending_changes === 'string' 
                                  ? selectedCinema.pending_changes 
                                  : JSON.stringify(selectedCinema.pending_changes, null, 2)}
                              </pre>
                            );
                          }
                        })()}
                      </div>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => approveProfileChanges(selectedCinema.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                        >
                          <Check className="w-4 h-4" /> Aprobar cambios
                        </button>
                        <button 
                          onClick={() => rejectProfileChanges(selectedCinema.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                        >
                          <X className="w-4 h-4" /> Rechazar cambios
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Datos de la Empresa - Accordion */}
                  <div className="bg-[#141414] rounded-xl overflow-hidden">
                    <button 
                      onClick={() => setOpenAccordionSections(prev => 
                        prev.includes('empresa') ? prev.filter(s => s !== 'empresa') : [...prev, 'empresa']
                      )}
                      className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-[#e4002b]" />
                        Datos de la Empresa
                      </h3>
                      <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${openAccordionSections.includes('empresa') ? 'rotate-180' : ''}`} />
                    </button>
                    {openAccordionSections.includes('empresa') && (
                      <div className="px-4 pb-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Razón Social</p>
                            <p className="text-white">{selectedCinema.company_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">CIF</p>
                            <p className="text-white">{selectedCinema.cif || '-'}</p>
                          </div>
                          {selectedCinema.management_type && (
                            <div>
                              <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Gestión</p>
                              <p className="text-white capitalize">{selectedCinema.management_type}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Registro</p>
                            <p className="text-white">{selectedCinema.created_at ? new Date(selectedCinema.created_at).toLocaleDateString('es-ES') : '-'}</p>
                          </div>
                        </div>
                        {selectedCinema.fiscal_address && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Domicilio Fiscal</p>
                            <p className="text-white text-sm">
                              {[selectedCinema.fiscal_address.street, selectedCinema.fiscal_address.city, selectedCinema.fiscal_address.postal_code].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Contacto - Accordion */}
                  <div className="bg-[#141414] rounded-xl overflow-hidden">
                    <button 
                      onClick={() => setOpenAccordionSections(prev => 
                        prev.includes('contacto') ? prev.filter(s => s !== 'contacto') : [...prev, 'contacto']
                      )}
                      className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-[#e4002b]" />
                        Datos de Contacto
                      </h3>
                      <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${openAccordionSections.includes('contacto') ? 'rotate-180' : ''}`} />
                    </button>
                    {openAccordionSections.includes('contacto') && (
                      <div className="px-4 pb-4">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Nombre</p>
                            <p className="text-white">{selectedCinema.contact_name || '-'}</p>
                          </div>
                          <div>
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Teléfono</p>
                            <p className="text-white">{selectedCinema.contact_phone || '-'}</p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Email</p>
                            <a href={`mailto:${selectedCinema.contact_email}`} className="text-[#e4002b] hover:underline">{selectedCinema.contact_email || '-'}</a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recintos y Salas - Accordion */}
                  {selectedCinema.venues && selectedCinema.venues.length > 0 ? (
                    (() => {
                      const venueIndex = selectedCinema.selectedVenueIndex || 0;
                      const venue = selectedCinema.venues[venueIndex];
                      if (!venue) return null;
                      
                      return (
                        <div className="bg-[#141414] rounded-xl overflow-hidden">
                          <button 
                            onClick={() => setOpenAccordionSections(prev => 
                              prev.includes('recinto') ? prev.filter(s => s !== 'recinto') : [...prev, 'recinto']
                            )}
                            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                          >
                            <h3 className="text-white font-semibold flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-[#e4002b]" />
                              Datos del Recinto
                              {selectedCinema.venues.length > 1 && (
                                <span className="text-xs text-white/50 font-normal">({venueIndex + 1}/{selectedCinema.venues.length})</span>
                              )}
                            </h3>
                            <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${openAccordionSections.includes('recinto') ? 'rotate-180' : ''}`} />
                          </button>
                          {openAccordionSections.includes('recinto') && (
                            <div className="px-4 pb-4">
                              <div className="mb-3">
                                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Nombre</p>
                                <p className="text-white font-semibold">{venue.venue_name || `Recinto ${venueIndex + 1}`}</p>
                              </div>
                              {venue.address && (
                                <div className="mb-3">
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Dirección</p>
                                  <p className="text-white text-sm">
                                    {[venue.address.street, venue.address.city, venue.address.postal_code].filter(Boolean).join(', ') || '-'}
                                  </p>
                                </div>
                              )}
                              {venue.rooms && venue.rooms.length > 0 && (
                                <div className="pt-3 border-t border-white/10">
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Salas ({venue.rooms.length})</p>
                                  <div className="space-y-2">
                                    {venue.rooms.map((room, roomIndex) => (
                                      <div key={roomIndex} className="bg-white/5 rounded-lg p-3 text-sm">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-white font-medium flex items-center gap-2">
                                            <Monitor className="w-4 h-4 text-[#e4002b]" />
                                            Sala {room.room_number || roomIndex + 1}
                                          </span>
                                          <span className="text-white/50 text-xs">{room.seating_capacity} butacas</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 text-xs">
                                          <div><span className="text-white/50">Proyector:</span> <span className="text-white">{room.projection_system || '-'}</span></div>
                                          <div><span className="text-white/50">Sonido:</span> <span className="text-white">{room.sound_system || '-'}</span></div>
                                          <div><span className="text-white/50">Internet:</span> <span className="text-white">{room.internet_connection || '-'}</span></div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    /* Legacy single venue display - Accordion */
                    <>
                      {/* Datos del Recinto (Legacy) - Accordion */}
                      <div className="bg-[#141414] rounded-xl overflow-hidden">
                        <button 
                          onClick={() => setOpenAccordionSections(prev => 
                            prev.includes('recinto') ? prev.filter(s => s !== 'recinto') : [...prev, 'recinto']
                          )}
                          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                        >
                          <h3 className="text-white font-semibold flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#e4002b]" />
                            Datos del Recinto
                          </h3>
                          <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${openAccordionSections.includes('recinto') ? 'rotate-180' : ''}`} />
                        </button>
                        {openAccordionSections.includes('recinto') && (
                          <div className="px-4 pb-4">
                            <div>
                              <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Nombre</p>
                              <p className="text-white font-medium">{selectedCinema.venue_name || '-'}</p>
                            </div>
                            {selectedCinema.address && (
                              <div className="mt-3">
                                <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Dirección</p>
                                <p className="text-white text-sm">
                                  {[selectedCinema.address.street, selectedCinema.address.city, selectedCinema.address.postal_code].filter(Boolean).join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Especificaciones Técnicas (Legacy) - Accordion */}
                      {selectedCinema.technical_specs && (
                        <div className="bg-[#141414] rounded-xl overflow-hidden">
                          <button 
                            onClick={() => setOpenAccordionSections(prev => 
                              prev.includes('tecnicas') ? prev.filter(s => s !== 'tecnicas') : [...prev, 'tecnicas']
                            )}
                            className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                          >
                            <h3 className="text-white font-semibold flex items-center gap-2">
                              <Monitor className="w-5 h-5 text-[#e4002b]" />
                              Especificaciones Técnicas
                            </h3>
                            <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${openAccordionSections.includes('tecnicas') ? 'rotate-180' : ''}`} />
                          </button>
                          {openAccordionSections.includes('tecnicas') && (
                            <div className="px-4 pb-4">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Proyección</p>
                                  <p className="text-white">{selectedCinema.technical_specs.projection_system || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Sonido</p>
                                  <p className="text-white">{selectedCinema.technical_specs.sound_system || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Pantalla</p>
                                  <p className="text-white">{selectedCinema.technical_specs.screen_size || '-'}</p>
                                </div>
                                <div>
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Aforo</p>
                                  <p className="text-white">{selectedCinema.technical_specs.seating_capacity || '-'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Actions - Use venue-specific status for multi-venue cinemas */}
                  {(() => {
                    const hasMultipleVenues = selectedCinema.venues && selectedCinema.venues.length > 0;
                    const venueIndex = selectedCinema.selectedVenueIndex;
                    const currentVenueStatus = hasMultipleVenues && venueIndex !== undefined
                      ? (selectedCinema.venues[venueIndex]?.status || 'pending')
                      : selectedCinema.status;
                    
                    return currentVenueStatus === 'pending' && (
                    <div className="flex gap-4 pt-4 border-t border-white/10">
                      <button 
                        onClick={() => { 
                          updateCinemaStatus(selectedCinema.id, 'approved', hasMultipleVenues ? venueIndex : null); 
                          setShowCinemaDetail(false); 
                        }} 
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500/20 text-green-400 rounded-lg font-medium hover:bg-green-500/30"
                      >
                        <Check className="w-5 h-5" /> Aprobar Recinto
                      </button>
                      <button 
                        onClick={() => { 
                          updateCinemaStatus(selectedCinema.id, 'rejected', hasMultipleVenues ? venueIndex : null); 
                          setShowCinemaDetail(false); 
                        }} 
                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-500/20 text-red-400 rounded-lg font-medium hover:bg-red-500/30"
                      >
                        <X className="w-5 h-5" /> Rechazar Recinto
                      </button>
                    </div>
                  );
                  })()}

                  {/* Actions for approved/inactive/rejected cinemas */}
                  {(() => {
                    const hasMultipleVenues = selectedCinema.venues && selectedCinema.venues.length > 0;
                    const venueIndex = selectedCinema.selectedVenueIndex;
                    const currentVenueStatus = hasMultipleVenues && venueIndex !== undefined
                      ? (selectedCinema.venues[venueIndex]?.status || 'pending')
                      : selectedCinema.status;
                    
                    return currentVenueStatus !== 'pending' && (
                    <div className="pt-4 border-t border-white/10">
                      <h4 className="text-white/50 text-xs uppercase tracking-wider mb-4">Acciones del recinto</h4>
                      <div className="flex gap-3 flex-wrap">
                        {currentVenueStatus === 'approved' && (
                          <button 
                            onClick={() => deactivateCinema(selectedCinema.id)} 
                            className="flex items-center gap-2 px-4 py-2 bg-gray-500/20 text-gray-400 rounded-lg hover:bg-gray-500/30 text-sm"
                          >
                            <EyeOff className="w-4 h-4" /> Inactivar cine
                          </button>
                        )}
                        {currentVenueStatus === 'inactive' && (
                          <button 
                            onClick={() => reactivateCinema(selectedCinema.id)} 
                            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 text-sm"
                          >
                            <Eye className="w-4 h-4" /> Reactivar cine
                          </button>
                        )}
                        <button 
                          onClick={() => deleteCinema(selectedCinema.id)} 
                          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm"
                        >
                          <Trash2 className="w-4 h-4" /> Eliminar permanentemente
                        </button>
                      </div>
                      {currentVenueStatus === 'approved' && (
                        <p className="text-white/40 text-xs mt-3">
                          <strong>Inactivar:</strong> El cine no podrá acceder al portal pero mantendrá sus datos.
                        </p>
                      )}
                      <p className="text-white/40 text-xs mt-1">
                        <strong>Eliminar:</strong> Se eliminará permanentemente. No se puede eliminar si tiene reservas activas.
                      </p>
                    </div>
                  );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* Bravo Number Modal */}
          {showBravoModal && selectedCinema && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowBravoModal(false)}>
              <div className="bg-[#1a1a1a] rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                <h2 className="text-white text-xl font-bold mb-2">Asignar Número de Bravo</h2>
                <p className="text-white/60 text-sm mb-6">
                  Introduce el número de Bravo para {
                    selectedCinema.venues && selectedCinema.venues.length > 0 && selectedCinema.selectedVenueIndex !== undefined
                      ? selectedCinema.venues[selectedCinema.selectedVenueIndex]?.venue_name || `Recinto ${selectedCinema.selectedVenueIndex + 1}`
                      : selectedCinema.venue_name
                  }
                </p>
                
                <div className="mb-6">
                  <label className="block text-white/50 text-xs uppercase tracking-wider mb-2">Número de Bravo</label>
                  <input 
                    type="text" 
                    value={bravoNumber}
                    onChange={(e) => setBravoNumber(e.target.value)}
                    placeholder="Ej: B-2024-0125"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-xl text-center tracking-widest focus:border-[#e4002b] focus:outline-none"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button 
                    onClick={() => setShowBravoModal(false)}
                    className="flex-1 px-4 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={assignBravoNumber}
                    disabled={!bravoNumber.trim() || savingBravo}
                    className="flex-1 px-4 py-3 bg-[#e4002b] text-white rounded-lg hover:bg-[#c70024] disabled:opacity-50 font-medium"
                  >
                    {savingBravo ? 'Guardando...' : 'Asignar número'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Change Request Approval/Rejection Modal */}
          {changeRequestModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-[#1a1a1a] rounded-xl max-w-md w-full p-6 border border-white/10">
                <h3 className="text-white text-lg font-semibold mb-2">
                  {changeRequestModal.action === 'approve' ? 'Aprobar solicitud' : 'Rechazar solicitud'}
                </h3>
                {changeRequestModal.contentTitle && (
                  <p className="text-white/60 text-sm mb-4">
                    Contenido: <span className="text-white">{changeRequestModal.contentTitle}</span>
                  </p>
                )}
                
                <div className="mb-4">
                  <label className="block text-white/70 text-sm mb-2">
                    {changeRequestModal.action === 'approve' ? 'Notas (opcional)' : 'Motivo del rechazo'}
                  </label>
                  <textarea
                    value={changeRequestNotes}
                    onChange={(e) => setChangeRequestNotes(e.target.value)}
                    placeholder={changeRequestModal.action === 'approve' 
                      ? 'Añade notas opcionales para el cine...' 
                      : 'Indica el motivo del rechazo...'}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:border-[#e4002b] focus:outline-none resize-none"
                    rows={3}
                  />
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => {
                      setChangeRequestModal(null);
                      setChangeRequestNotes('');
                    }}
                    disabled={processingChangeRequest}
                    className="flex-1 px-4 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 font-medium"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={() => {
                      if (changeRequestModal.action === 'reject' && !changeRequestNotes.trim()) {
                        return; // Require notes for rejection
                      }
                      processChangeRequest(changeRequestModal.requestId, changeRequestModal.action, changeRequestNotes);
                    }}
                    disabled={processingChangeRequest || (changeRequestModal.action === 'reject' && !changeRequestNotes.trim())}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 ${
                      changeRequestModal.action === 'approve' 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >
                    {processingChangeRequest 
                      ? 'Procesando...' 
                      : changeRequestModal.action === 'approve' ? 'Aprobar' : 'Rechazar'}
                  </button>
                </div>
                
                {changeRequestModal.action === 'reject' && !changeRequestNotes.trim() && (
                  <p className="text-red-400 text-xs mt-2">* El motivo del rechazo es obligatorio</p>
                )}
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmModal && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setDeleteConfirmModal(null)}>
              <div className="bg-[#1a1a1a] rounded-xl max-w-md w-full p-6 border border-white/10" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${deleteConfirmModal.permanent ? 'bg-red-600/20' : 'bg-yellow-500/20'}`}>
                    <Trash2 className={`w-6 h-6 ${deleteConfirmModal.permanent ? 'text-red-500' : 'text-yellow-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-white text-lg font-semibold">
                      {deleteConfirmModal.permanent ? 'Eliminar permanentemente' : 'Mover a papelera'}
                    </h3>
                    <p className="text-white/50 text-sm">{deleteConfirmModal.name}</p>
                  </div>
                </div>
                
                <p className="text-white/70 text-sm mb-6">
                  {deleteConfirmModal.permanent 
                    ? '¿Estás seguro de que deseas eliminar permanentemente este elemento? Esta acción no se puede deshacer.'
                    : '¿Estás seguro de que deseas mover este elemento a la papelera? Podrás restaurarlo más tarde.'}
                </p>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => setDeleteConfirmModal(null)}
                    disabled={deleting}
                    className="flex-1 px-4 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 font-medium"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={() => handleDelete(deleteConfirmModal.type, deleteConfirmModal.id, deleteConfirmModal.permanent)}
                    disabled={deleting}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 ${
                      deleteConfirmModal.permanent 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-yellow-600 text-white hover:bg-yellow-700'
                    }`}
                  >
                    {deleting ? 'Procesando...' : deleteConfirmModal.permanent ? 'Eliminar' : 'Mover a papelera'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'bookings' && (
            <div>
              {/* Status Tabs */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'pending', 'approved', 'rejected', 'completed', 'deleted', 'calendar'].map(status => (
                  <button key={status} onClick={() => setFilterStatus(status)} className={`px-4 py-2 rounded-lg text-sm font-medium ${filterStatus === status ? (status === 'deleted' ? 'bg-red-600 text-white' : 'bg-[#e4002b] text-white') : 'bg-white/5 text-white/60 hover:bg-white/10'}`}>
                    {status === 'all' ? 'Todas' : status === 'calendar' ? 'Calendario' : status === 'deleted' ? 'Papelera' : statusLabels[status] || status}
                    {status === 'deleted' && <Trash2 className="w-3 h-3 ml-1 inline" />}
                  </button>
                ))}
              </div>

              {/* Filters Row */}
              {filterStatus !== 'calendar' && (
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-[#141414] rounded-xl">
                  {/* Month Filter */}
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-white/50 text-xs mb-1 block">Mes</label>
                    <select 
                      value={bookingFilters.month}
                      onChange={(e) => setBookingFilters({...bookingFilters, month: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                    >
                      <option value="">Todos los meses</option>
                      {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((m, i) => (
                        <option key={i+1} value={String(i+1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Cinema Filter */}
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-white/50 text-xs mb-1 block">Cine</label>
                    <select 
                      value={bookingFilters.cinema_id}
                      onChange={(e) => setBookingFilters({...bookingFilters, cinema_id: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                    >
                      <option value="">Todos los cines</option>
                      {cinemas.filter(c => c.status === 'approved').map(c => (
                        <option key={c.id} value={c.id}>{c.bravo_number ? `[${c.bravo_number}] ` : ''}{c.venue_name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Content Filter */}
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-white/50 text-xs mb-1 block">Contenido</label>
                    <select 
                      value={bookingFilters.content_id}
                      onChange={(e) => setBookingFilters({...bookingFilters, content_id: e.target.value})}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-[#e4002b] focus:outline-none"
                    >
                      <option value="">Todos los contenidos</option>
                      {contents.map(c => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Clear Filters */}
                  {(bookingFilters.month || bookingFilters.cinema_id || bookingFilters.content_id) && (
                    <div className="flex items-end">
                      <button 
                        onClick={() => setBookingFilters({ month: '', cinema_id: '', content_id: '' })}
                        className="px-3 py-2 text-white/60 hover:text-white text-sm"
                      >
                        Limpiar filtros
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Calendar View */}
              {filterStatus === 'calendar' ? (
                <BookingsCalendarView bookings={bookings} contents={contents} cinemas={cinemas} />
              ) : (
              <div className="space-y-4">
                {bookings
                  .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                  .filter(b => filterStatus === 'all' ? b.status !== 'deleted' : b.status === filterStatus)
                  .filter(b => !bookingFilters.cinema_id || b.cinema_id === bookingFilters.cinema_id)
                  .filter(b => !bookingFilters.content_id || b.content_id === bookingFilters.content_id)
                  .filter(b => {
                    if (!bookingFilters.month) return true;
                    return b.screenings?.some(s => {
                      if (s.date.includes('/')) {
                        const [d, m, y] = s.date.split('/');
                        return m === bookingFilters.month;
                      } else if (s.date.includes('-')) {
                        const [y, m, d] = s.date.split('-');
                        return m === bookingFilters.month;
                      }
                      return false;
                    });
                  })
                  .map(b => {
                  const content = contents.find(c => c.id === b.content_id);
                  const cinema = cinemas.find(c => c.id === b.cinema_id);
                  return (
                    <div key={b.id} className={`bg-[#141414] rounded-xl p-6 ${b.status === 'deleted' ? 'opacity-70' : ''}`}>
                      <div className="flex items-start gap-4">
                        {content && <img src={content.hero_image || content.image} alt={content.title} className="w-24 h-32 object-cover rounded-lg" />}
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-white font-semibold text-lg">{content?.title || 'N/A'}</h3>
                              <p className="text-[#e4002b] text-sm">{content?.composer}</p>
                              <p className="text-white/50 text-sm mt-1">
                                Cine: {cinema?.bravo_number && <span className="text-[#e4002b] font-medium">[{cinema.bravo_number}] </span>}
                                {cinema?.venue_name || 'N/A'}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded border text-xs font-medium ${statusColors[b.status]}`}>{statusLabels[b.status] || b.status}</span>
                          </div>
                          <div className="mt-4">
                            <p className="text-white/60 text-sm mb-2">Sesiones:</p>
                            <div className="flex flex-wrap gap-2">{b.screenings?.map((s, i) => <span key={i} className="px-3 py-1 bg-white/5 rounded text-white/80 text-sm">{s.date} {s.time}</span>)}</div>
                          </div>
                          {b.status === 'pending' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                              <button onClick={() => updateBookingStatus(b.id, 'approved', 'Reserva aprobada')} className="px-4 py-2 bg-green-500/20 text-green-400 rounded text-sm font-medium hover:bg-green-500/30">Aprobar</button>
                              <button onClick={() => updateBookingStatus(b.id, 'rejected', 'Reserva rechazada')} className="px-4 py-2 bg-red-500/20 text-red-400 rounded text-sm font-medium hover:bg-red-500/30">Rechazar</button>
                              <button onClick={() => setDeleteConfirmModal({ type: 'booking', id: b.id, name: content?.title || 'Reserva', permanent: false })} className="px-4 py-2 bg-red-500/10 text-red-400 rounded text-sm font-medium hover:bg-red-500/20 ml-auto">
                                <Trash2 className="w-4 h-4 inline mr-1" /> Eliminar
                              </button>
                            </div>
                          )}
                          {b.status === 'deleted' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                              <button onClick={() => handleRestore('booking', b.id)} className="px-4 py-2 bg-green-500/20 text-green-400 rounded text-sm font-medium hover:bg-green-500/30">
                                <RotateCcw className="w-4 h-4 inline mr-1" /> Restaurar
                              </button>
                              <button onClick={() => setDeleteConfirmModal({ type: 'booking', id: b.id, name: content?.title || 'Reserva', permanent: true })} className="px-4 py-2 bg-red-600/30 text-red-400 rounded text-sm font-medium hover:bg-red-600/50">
                                <Trash2 className="w-4 h-4 inline mr-1" /> Eliminar permanentemente
                              </button>
                            </div>
                          )}
                          {(b.status === 'approved' || b.status === 'rejected' || b.status === 'completed') && (
                            <div className="flex gap-2 mt-4 pt-4 border-t border-white/10">
                              <button onClick={() => setDeleteConfirmModal({ type: 'booking', id: b.id, name: content?.title || 'Reserva', permanent: false })} className="px-4 py-2 bg-red-500/10 text-red-400 rounded text-sm font-medium hover:bg-red-500/20 ml-auto">
                                <Trash2 className="w-4 h-4 inline mr-1" /> Eliminar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {bookings
                  .filter(b => filterStatus === 'all' ? b.status !== 'deleted' : b.status === filterStatus)
                  .length === 0 && (
                  <div className="bg-[#141414] rounded-xl p-12 text-center">
                    <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">{filterStatus === 'deleted' ? 'No hay reservas en la papelera' : 'No hay reservas'}</p>
                  </div>
                )}
              </div>
              )}
            </div>
          )}

          {/* Inquiries Section - Special Economic Conditions */}
          {activeSection === 'inquiries' && (
            <div>
              <div className="mb-6">
                <h2 className="text-white text-xl font-semibold mb-2">Consultas de Condiciones Especiales</h2>
                <p className="text-white/50 text-sm">Gestiona las solicitudes de cines para contenido con condiciones económicas especiales.</p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Pendientes</p>
                  <p className="text-yellow-400 text-2xl font-bold">{inquiries.filter(i => i.status === 'pending').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Respondidas</p>
                  <p className="text-blue-400 text-2xl font-bold">{inquiries.filter(i => i.status === 'responded').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Aceptadas</p>
                  <p className="text-green-400 text-2xl font-bold">{inquiries.filter(i => i.status === 'accepted').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Total</p>
                  <p className="text-white text-2xl font-bold">{inquiries.filter(i => i.status !== 'deleted').length}</p>
                </div>
              </div>

              {/* Status Tabs */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'pending', 'responded', 'accepted', 'rejected', 'deleted'].map(status => (
                  <button 
                    key={status} 
                    onClick={() => setInquiriesTab(status)} 
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      inquiriesTab === status 
                        ? (status === 'deleted' ? 'bg-red-600 text-white' : 'bg-[#e4002b] text-white') 
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    {status === 'all' ? 'Todas' : status === 'deleted' ? 'Papelera' : status === 'pending' ? 'Pendientes' : status === 'responded' ? 'Oferta enviada' : status === 'accepted' ? 'Aceptadas' : 'Rechazadas'}
                    {status === 'deleted' && <Trash2 className="w-3 h-3 ml-1 inline" />}
                  </button>
                ))}
              </div>

              {/* Inquiries List */}
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-white font-semibold">Consultas Recibidas</h3>
                </div>
                
                {inquiries.filter(i => inquiriesTab === 'all' ? i.status !== 'deleted' : i.status === inquiriesTab).length === 0 ? (
                  <div className="p-12 text-center">
                    <AlertCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">{inquiriesTab === 'deleted' ? 'No hay consultas en la papelera' : 'No hay consultas de condiciones especiales'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {inquiries
                      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                      .filter(i => inquiriesTab === 'all' ? i.status !== 'deleted' : i.status === inquiriesTab)
                      .map(inquiry => {
                      const content = contents.find(c => c.id === inquiry.content_id);
                      const cinema = cinemas.find(c => c.id === inquiry.cinema_id);
                      
                      const statusColors = {
                        pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                        responded: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                        accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
                        rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
                        deleted: 'bg-red-500/20 text-red-300 border-red-500/30'
                      };
                      const statusLabels = {
                        pending: 'Pendiente',
                        responded: 'Oferta enviada',
                        accepted: 'Aceptada',
                        rejected: 'Rechazada',
                        deleted: 'Eliminada'
                      };
                      
                      return (
                        <div key={inquiry.id} className={`p-4 hover:bg-white/5 ${inquiry.status === 'deleted' ? 'opacity-70' : ''}`}>
                          <div className="flex items-start gap-4">
                            {/* Content Image */}
                            {content && (
                              <img 
                                src={content.hero_image || content.image} 
                                alt={content.title} 
                                className="w-20 h-28 object-cover rounded"
                              />
                            )}
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="text-white font-medium">{content?.title || 'Contenido no encontrado'}</h4>
                                  <p className="text-[#e4002b] text-sm">{content?.composer}</p>
                                </div>
                                <span className={`px-2 py-1 rounded border text-xs font-medium ${statusColors[inquiry.status]}`}>
                                  {statusLabels[inquiry.status]}
                                </span>
                              </div>
                              
                              <div className="text-white/60 text-sm mb-2">
                                <span className="font-medium text-white">{cinema?.venue_name}</span>
                                <span className="mx-2">·</span>
                                <span>{cinema?.company_name}</span>
                              </div>
                              
                              <p className="text-white/40 text-xs">
                                Solicitado: {new Date(inquiry.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                              
                              {/* Show response if responded */}
                              {inquiry.status !== 'pending' && inquiry.pricing_type && (
                                <div className="mt-3 p-3 bg-white/5 rounded-lg">
                                  <p className="text-white/60 text-xs uppercase tracking-wider mb-1">Oferta enviada</p>
                                  <p className="text-white font-medium">
                                    {inquiry.pricing_type === 'tanto_alzado' 
                                      ? `${inquiry.flat_fee_price}€ + IVA`
                                      : `${inquiry.exhibitor_percentage}% para el exhibidor`
                                    }
                                  </p>
                                  {inquiry.admin_response && (
                                    <p className="text-white/50 text-sm mt-1">{inquiry.admin_response}</p>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-2">
                              {inquiry.status === 'pending' && (
                                <button
                                  onClick={() => {
                                    setSelectedInquiry(inquiry);
                                    setInquiryResponse({
                                      admin_response: '',
                                      pricing_type: 'tanto_alzado',
                                      exhibitor_percentage: 50,
                                      flat_fee_price: 400
                                    });
                                  }}
                                  className="px-4 py-2 bg-[#e4002b] text-white text-sm rounded-lg hover:bg-[#c70024]"
                                >
                                  Responder
                                </button>
                              )}
                              {inquiry.status === 'deleted' ? (
                                <>
                                  <button 
                                    onClick={() => handleRestore('inquiry', inquiry.id)} 
                                    className="p-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30"
                                    title="Restaurar"
                                  >
                                    <RotateCcw className="w-4 h-4" />
                                  </button>
                                  <button 
                                    onClick={() => setDeleteConfirmModal({ type: 'inquiry', id: inquiry.id, name: content?.title || 'Consulta', permanent: true })} 
                                    className="p-2 bg-red-600/30 text-red-400 rounded-lg hover:bg-red-600/50"
                                    title="Eliminar permanentemente"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <button 
                                  onClick={() => setDeleteConfirmModal({ type: 'inquiry', id: inquiry.id, name: content?.title || 'Consulta', permanent: false })} 
                                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                                  title="Mover a papelera"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Inquiry Response Modal */}
          {selectedInquiry && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedInquiry(null)}>
              <div className="bg-[#1a1a1a] rounded-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="border-b border-white/10 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-white text-xl font-bold">Responder Consulta</h2>
                      <p className="text-white/50 text-sm">Envía una oferta de condiciones especiales</p>
                    </div>
                    <button onClick={() => setSelectedInquiry(null)} className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Content & Cinema Info */}
                  <div className="flex gap-4 p-4 bg-white/5 rounded-lg">
                    {(() => {
                      const content = contents.find(c => c.id === selectedInquiry.content_id);
                      const cinema = cinemas.find(c => c.id === selectedInquiry.cinema_id);
                      return (
                        <>
                          {content && (
                            <img src={content.hero_image || content.image} alt="" className="w-16 h-20 object-cover rounded" />
                          )}
                          <div>
                            <p className="text-white font-medium">{content?.title}</p>
                            <p className="text-[#e4002b] text-sm">{content?.composer}</p>
                            <p className="text-white/50 text-sm mt-1">{cinema?.venue_name}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Pricing Type Selection */}
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Tipo de condición</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setInquiryResponse(prev => ({ ...prev, pricing_type: 'tanto_alzado' }))}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          inquiryResponse.pricing_type === 'tanto_alzado' 
                            ? 'border-[#e4002b] bg-[#e4002b]/10' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <DollarSign className={`w-5 h-5 mb-2 ${inquiryResponse.pricing_type === 'tanto_alzado' ? 'text-[#e4002b]' : 'text-white/50'}`} />
                        <p className="text-white font-medium text-sm">Tanto Alzado</p>
                        <p className="text-white/50 text-xs">Flat Fee</p>
                      </button>
                      <button
                        onClick={() => setInquiryResponse(prev => ({ ...prev, pricing_type: 'porcentaje_taquilla' }))}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          inquiryResponse.pricing_type === 'porcentaje_taquilla' 
                            ? 'border-blue-500 bg-blue-500/10' 
                            : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <Percent className={`w-5 h-5 mb-2 ${inquiryResponse.pricing_type === 'porcentaje_taquilla' ? 'text-blue-400' : 'text-white/50'}`} />
                        <p className="text-white font-medium text-sm">% Taquilla</p>
                        <p className="text-white/50 text-xs">Box Office</p>
                      </button>
                    </div>
                  </div>
                  
                  {/* Price Input */}
                  {inquiryResponse.pricing_type === 'tanto_alzado' ? (
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Precio (€ + IVA)</label>
                      <input
                        type="number"
                        value={inquiryResponse.flat_fee_price}
                        onChange={(e) => setInquiryResponse(prev => ({ ...prev, flat_fee_price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-xl font-bold focus:border-[#e4002b] focus:outline-none"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="text-white/60 text-sm mb-2 block">Porcentaje para el Exhibidor</label>
                      <div className="flex gap-2">
                        {[45, 50, 55].map(pct => (
                          <button
                            key={pct}
                            onClick={() => setInquiryResponse(prev => ({ ...prev, exhibitor_percentage: pct }))}
                            className={`flex-1 px-4 py-3 rounded-lg border text-lg font-bold ${
                              inquiryResponse.exhibitor_percentage === pct 
                                ? 'border-blue-500 bg-blue-500/20 text-white' 
                                : 'border-white/10 text-white/60'
                            }`}
                          >
                            {pct}%
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Message */}
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Mensaje (opcional)</label>
                    <textarea
                      value={inquiryResponse.admin_response}
                      onChange={(e) => setInquiryResponse(prev => ({ ...prev, admin_response: e.target.value }))}
                      placeholder="Añade un mensaje explicativo para el cine..."
                      rows={3}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-[#e4002b] focus:outline-none resize-none"
                    />
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSelectedInquiry(null)}
                      className="flex-1 py-3 bg-white/10 text-white rounded-lg hover:bg-white/20"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={respondToInquiry}
                      disabled={savingInquiry}
                      className="flex-1 py-3 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] disabled:opacity-50"
                    >
                      {savingInquiry ? 'Enviando...' : 'Enviar Oferta'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Change Requests Section */}
          {activeSection === 'change-requests' && (
            <div>
              <div className="mb-6">
                <h2 className="text-white text-xl font-semibold mb-2">Solicitudes de Modificación/Cancelación</h2>
                <p className="text-white/50 text-sm">Gestiona las solicitudes de cambio enviadas por los cines.</p>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Pendientes</p>
                  <p className="text-yellow-400 text-2xl font-bold">{changeRequests.filter(r => r.status === 'pending').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Aprobadas</p>
                  <p className="text-green-400 text-2xl font-bold">{changeRequests.filter(r => r.status === 'approved').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Rechazadas</p>
                  <p className="text-red-400 text-2xl font-bold">{changeRequests.filter(r => r.status === 'rejected').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Total</p>
                  <p className="text-white text-2xl font-bold">{changeRequests.filter(r => r.status !== 'deleted').length}</p>
                </div>
              </div>

              {/* Status Tabs */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {['all', 'pending', 'approved', 'rejected', 'deleted'].map(status => (
                  <button 
                    key={status} 
                    onClick={() => setChangeRequestsTab(status)} 
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      changeRequestsTab === status 
                        ? (status === 'deleted' ? 'bg-red-600 text-white' : 'bg-[#e4002b] text-white') 
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    {status === 'all' ? 'Todas' : status === 'deleted' ? 'Papelera' : status === 'pending' ? 'Pendientes' : status === 'approved' ? 'Aprobadas' : 'Rechazadas'}
                    {status === 'deleted' && <Trash2 className="w-3 h-3 ml-1 inline" />}
                  </button>
                ))}
              </div>

              {/* Requests List */}
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-white font-semibold">Solicitudes Recibidas</h3>
                </div>
                
                {changeRequests.filter(r => changeRequestsTab === 'all' ? r.status !== 'deleted' : r.status === changeRequestsTab).length === 0 ? (
                  <div className="p-12 text-center">
                    <AlertCircle className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">{changeRequestsTab === 'deleted' ? 'No hay solicitudes en la papelera' : 'No hay solicitudes de cambio'}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {changeRequests
                      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
                      .filter(r => changeRequestsTab === 'all' ? r.status !== 'deleted' : r.status === changeRequestsTab)
                      .map(req => {
                      const statusColors = {
                        pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                        approved: 'bg-green-500/20 text-green-400 border-green-500/30',
                        rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
                        deleted: 'bg-red-500/20 text-red-300 border-red-500/30'
                      };
                      const statusLabels = {
                        pending: 'Pendiente',
                        approved: 'Aprobada',
                        rejected: 'Rechazada',
                        deleted: 'Eliminada'
                      };
                      const typeLabels = {
                        modification: 'Modificación',
                        cancellation: 'Cancelación'
                      };
                      const typeColors = {
                        modification: 'text-blue-400',
                        cancellation: 'text-red-400'
                      };
                      
                      return (
                        <div key={req.id} className={`p-4 hover:bg-white/5 ${req.status === 'deleted' ? 'opacity-70' : ''}`}>
                          <div className="flex items-start gap-4">
                            {req.content_image && (
                              <img src={req.content_image} alt="" className="w-16 h-24 object-cover rounded" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="text-white font-medium">{req.content_title}</h4>
                                  <p className={`text-sm font-medium ${typeColors[req.request_type]}`}>
                                    Solicitud de {typeLabels[req.request_type]}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded border text-xs font-medium ${statusColors[req.status]}`}>
                                  {statusLabels[req.status]}
                                </span>
                              </div>
                              
                              <div className="text-white/60 text-sm mb-2">
                                <span className="font-medium text-white">{req.cinema_name}</span>
                                <span className="mx-2">·</span>
                                <span>{new Date(req.created_at).toLocaleDateString('es-ES')}</span>
                              </div>
                              
                              {req.reason && (
                                <p className="text-white/40 text-sm mb-2">
                                  <span className="text-white/60">Motivo:</span> {req.reason}
                                </p>
                              )}
                              
                              {req.request_type === 'modification' && req.new_screenings && (
                                <div className="text-white/40 text-xs mb-2">
                                  <span className="text-white/60">Nuevas fechas:</span>{' '}
                                  {req.new_screenings.map((s, i) => (
                                    <span key={i} className="mr-2">{s.date} {s.time}</span>
                                  ))}
                                </div>
                              )}
                              
                              <div className="flex gap-2 mt-3">
                                {req.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => {
                                        setChangeRequestModal({ requestId: req.id, action: 'approve', contentTitle: req.content_title });
                                        setChangeRequestNotes('');
                                      }}
                                      className="px-3 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded text-sm flex items-center gap-1"
                                    >
                                      <Check className="w-3 h-3" /> Aprobar
                                    </button>
                                    <button
                                      onClick={() => {
                                        setChangeRequestModal({ requestId: req.id, action: 'reject', contentTitle: req.content_title });
                                        setChangeRequestNotes('');
                                      }}
                                      className="px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-sm flex items-center gap-1"
                                    >
                                      <X className="w-3 h-3" /> Rechazar
                                    </button>
                                  </>
                                )}
                                {req.status === 'deleted' ? (
                                  <>
                                    <button 
                                      onClick={() => handleRestore('change-request', req.id)} 
                                      className="px-3 py-1.5 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded text-sm flex items-center gap-1"
                                    >
                                      <RotateCcw className="w-3 h-3" /> Restaurar
                                    </button>
                                    <button 
                                      onClick={() => setDeleteConfirmModal({ type: 'change-request', id: req.id, name: req.content_title || 'Solicitud', permanent: true })} 
                                      className="px-3 py-1.5 bg-red-600/30 text-red-400 hover:bg-red-600/50 rounded text-sm flex items-center gap-1"
                                    >
                                      <Trash2 className="w-3 h-3" /> Eliminar
                                    </button>
                                  </>
                                ) : (
                                  <button 
                                    onClick={() => setDeleteConfirmModal({ type: 'change-request', id: req.id, name: req.content_title || 'Solicitud', permanent: false })} 
                                    className="px-3 py-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded text-sm flex items-center gap-1 ml-auto"
                                  >
                                    <Trash2 className="w-3 h-3" /> Eliminar
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conditions Section */}
          {activeSection === 'conditions' && (
            <div>
              <div className="mb-6">
                <h2 className="text-white text-lg font-semibold mb-2">Condiciones Económicas</h2>
                <p className="text-white/60 text-sm">Gestiona las tarifas y condiciones comerciales para cada cine.</p>
              </div>

              {/* Pricing Reference Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Porcentaje de Taquilla */}
                <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Percent className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Porcentaje de Taquilla</h3>
                      <p className="text-white/50 text-xs">Box Office Split</p>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm mb-4">
                    Reparto de ingresos brutos de taquilla (deducido IVA).
                  </p>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">45% Exhibidor / 55% Distribuidora</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">50% Exhibidor / 50% Distribuidora</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">55% Exhibidor / 45% Distribuidora</span>
                    </div>
                  </div>
                </div>

                {/* Tanto Alzado */}
                <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#e4002b]/20 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-[#e4002b]" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Tanto Alzado</h3>
                      <p className="text-white/50 text-xs">Flat Fee (+ IVA)</p>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="pb-2 border-b border-white/10">
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Ópera, Ballet, Concierto</p>
                      <div className="grid grid-cols-3 gap-2 text-white/70">
                        <span>Directo: 450€/350€</span>
                        <span>Encore: 400€/300€</span>
                        <span>Grabado: 350€/250€</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Película, Documental</p>
                      <div className="grid grid-cols-2 gap-2 text-white/70">
                        <span>Película: 350€/250€</span>
                        <span>Documental: 200€/150€</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cinemas with Pricing */}
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-white font-semibold">Condiciones por Cine</h3>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-white/50 text-xs font-medium px-6 py-4">CINE</th>
                      <th className="text-left text-white/50 text-xs font-medium px-6 py-4">ESTADO</th>
                      <th className="text-left text-white/50 text-xs font-medium px-6 py-4">TIPO TARIFA</th>
                      <th className="text-left text-white/50 text-xs font-medium px-6 py-4">ALQUILER BRAVO</th>
                      <th className="text-left text-white/50 text-xs font-medium px-6 py-4">CONFIGURACIÓN</th>
                      <th className="text-right text-white/50 text-xs font-medium px-6 py-4">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cinemas.flatMap(cinema => {
                      // If cinema has multiple venues, create a row for each approved venue
                      const hasMultipleVenues = cinema.venues && cinema.venues.length > 0;
                      const venues = hasMultipleVenues 
                        ? cinema.venues 
                        : [{ venue_name: cinema.venue_name, status: cinema.status, bravo_number: cinema.bravo_number }];
                      
                      return venues
                        .filter(venue => (venue.status || cinema.status) === 'approved')
                        .map((venue, venueIdx) => {
                          const actualVenueIndex = hasMultipleVenues ? cinema.venues.findIndex(v => v === venue) : 0;
                          // Create unique pricing key for multi-venue: cinema_id + "_venue_" + venueIndex
                          const pricingKey = hasMultipleVenues ? `${cinema.id}_venue_${actualVenueIndex}` : cinema.id;
                          const pricing = cinemaPricings[pricingKey] || cinemaPricings[cinema.id]; // Fallback to cinema-level pricing
                          const bravoRentalLabel = pricing?.bravo_rental ? (
                            pricing.bravo_rental.mode === 'por_evento' ? 'Por Evento (50€)' :
                            pricing.bravo_rental.mode === 'anual_3_years' ? `Premium 2 años (${pricing.bravo_rental.payment_type === 'mensual' ? '20€/mes' : '150€'})` :
                            pricing.bravo_rental.mode === 'anual_1_year' ? `Flexible 1 año (${pricing.bravo_rental.payment_type === 'mensual' ? '25€/mes' : '195€'})` : '-'
                          ) : '-';
                          
                          return (
                            <tr key={`${cinema.id}-venue-${actualVenueIndex}`} className="border-b border-white/5 hover:bg-white/5">
                              <td className="px-6 py-4">
                                <p className="text-white font-medium">{venue.venue_name || cinema.venue_name}</p>
                                <p className="text-white/50 text-sm">{cinema.company_name}</p>
                                {hasMultipleVenues && (
                                  <span className="text-xs text-[#e4002b] bg-[#e4002b]/10 px-2 py-0.5 rounded mt-1 inline-block">
                                    Recinto {actualVenueIndex + 1} de {cinema.venues.length}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {pricing ? (
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded font-medium">Configurado</span>
                                ) : (
                                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded font-medium">Sin configurar</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-white/70 text-sm">
                                {pricing?.pricing_type === 'porcentaje_taquilla' ? 'Porcentaje Taquilla' : 
                                 pricing?.pricing_type === 'tanto_alzado' ? 'Tanto Alzado' : '-'}
                              </td>
                              <td className="px-6 py-4 text-white/70 text-sm">
                                <span className={pricing?.bravo_rental ? 'text-[#e4002b] font-medium' : 'text-white/50'}>
                                  {bravoRentalLabel}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-white/60 text-sm">
                                {pricing?.pricing_type === 'porcentaje_taquilla' && (
                                  <span>{pricing.box_office?.exhibitor_percentage}% Exhibidor</span>
                                )}
                                {pricing?.pricing_type === 'tanto_alzado' && (
                                  <span>Directo: {pricing.flat_fee?.directo_opera_ballet_concert}€</span>
                                )}
                                {!pricing && '-'}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <button
                                  onClick={() => {
                                    setSelectedCinema({
                                      ...cinema,
                                      selectedVenueIndex: actualVenueIndex,
                                      displayVenueName: venue.venue_name || cinema.venue_name,
                                      pricingKey: pricingKey
                                    });
                                    if (pricing) {
                                      setPricingForm({
                                        pricing_type: pricing.pricing_type,
                                        exhibitor_percentage: pricing.box_office?.exhibitor_percentage || 50,
                                        directo_opera_ballet_concert: pricing.flat_fee?.directo_opera_ballet_concert || 450,
                                        encore_opera_ballet_concert: pricing.flat_fee?.encore_opera_ballet_concert || 400,
                                        grabado_opera_ballet_concert: pricing.flat_fee?.grabado_opera_ballet_concert || 350,
                                        pelicula: pricing.flat_fee?.pelicula || 350,
                                        documental: pricing.flat_fee?.documental || 200,
                                        bravo_rental_mode: pricing.bravo_rental?.mode || 'por_evento',
                                        bravo_rental_payment: pricing.bravo_rental?.payment_type || 'unico',
                                        bravo_rental_price: pricing.bravo_rental?.price_per_event || 50
                                      });
                                    } else {
                                      setPricingForm({
                                        pricing_type: 'tanto_alzado',
                                        exhibitor_percentage: 50,
                                        directo_opera_ballet_concert: 450,
                                        encore_opera_ballet_concert: 400,
                                        grabado_opera_ballet_concert: 350,
                                        pelicula: 350,
                                        documental: 200,
                                        bravo_rental_mode: 'por_evento',
                                        bravo_rental_payment: 'unico',
                                        bravo_rental_price: 50
                                      });
                                    }
                                    setSelectedCinemaPricing(pricing);
                                    setShowCinemaDetail(true);
                                  }}
                                  className="px-3 py-1.5 bg-[#e4002b] text-white text-sm rounded hover:bg-[#c70024]"
                                >
                                  {pricing ? 'Editar' : 'Configurar'}
                                </button>
                              </td>
                            </tr>
                          );
                        });
                    })}
                  </tbody>
                </table>
                {cinemas.flatMap(c => {
                  const hasMultipleVenues = c.venues && c.venues.length > 0;
                  const venues = hasMultipleVenues ? c.venues : [{ status: c.status }];
                  return venues.filter(v => (v.status || c.status) === 'approved');
                }).length === 0 && (
                  <div className="p-12 text-center">
                    <Building2 className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No hay cines aprobados para configurar</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pricing Configuration Modal */}
          {activeSection === 'conditions' && showCinemaDetail && selectedCinema && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCinemaDetail(false)}>
              <div className="bg-[#1a1a1a] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-[#1a1a1a] border-b border-white/10 p-6 flex items-center justify-between flex-shrink-0">
                  <div>
                    <h2 className="text-white text-xl font-bold">Condiciones Económicas</h2>
                    <p className="text-white/50 text-sm">{selectedCinema.displayVenueName || selectedCinema.venue_name}</p>
                    {selectedCinema.selectedVenueIndex !== undefined && selectedCinema.venues && (
                      <span className="text-xs text-[#e4002b] bg-[#e4002b]/10 px-2 py-0.5 rounded mt-1 inline-block">
                        Recinto {selectedCinema.selectedVenueIndex + 1} de {selectedCinema.venues.length}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setShowCinemaDetail(false)} className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Tabs */}
                <div className="flex border-b border-white/10 flex-shrink-0">
                  <button
                    onClick={() => setPricingModalTab('tarifa')}
                    className={`flex-1 py-4 text-sm font-medium transition-colors ${
                      pricingModalTab === 'tarifa' 
                        ? 'text-[#e4002b] border-b-2 border-[#e4002b] bg-[#e4002b]/5' 
                        : 'text-white/50 hover:text-white/80'
                    }`}
                  >
                    <DollarSign className="w-4 h-4 inline mr-2" />
                    Tipo de Tarifa
                  </button>
                  <button
                    onClick={() => setPricingModalTab('bravo')}
                    className={`flex-1 py-4 text-sm font-medium transition-colors ${
                      pricingModalTab === 'bravo' 
                        ? 'text-[#e4002b] border-b-2 border-[#e4002b] bg-[#e4002b]/5' 
                        : 'text-white/50 hover:text-white/80'
                    }`}
                  >
                    <Box className="w-4 h-4 inline mr-2" />
                    Alquiler Bravo
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="overflow-y-auto flex-1 p-6">
                  {/* Tab: Tipo de Tarifa */}
                  {pricingModalTab === 'tarifa' && (
                    <div className="space-y-6">
                      {/* Pricing Type Selection */}
                      <div>
                        <label className="text-white/60 text-sm mb-2 block">Tipo de tarifa</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => setPricingForm(prev => ({ ...prev, pricing_type: 'porcentaje_taquilla' }))}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              pricingForm.pricing_type === 'porcentaje_taquilla' 
                                ? 'border-blue-500 bg-blue-500/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <Percent className={`w-6 h-6 mb-2 ${pricingForm.pricing_type === 'porcentaje_taquilla' ? 'text-blue-400' : 'text-white/50'}`} />
                            <p className="text-white font-medium">Porcentaje de Taquilla</p>
                            <p className="text-white/50 text-xs">Box Office Split</p>
                          </button>
                          <button
                            onClick={() => setPricingForm(prev => ({ ...prev, pricing_type: 'tanto_alzado' }))}
                            className={`p-4 rounded-xl border text-left transition-all ${
                              pricingForm.pricing_type === 'tanto_alzado' 
                                ? 'border-[#e4002b] bg-[#e4002b]/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <DollarSign className={`w-6 h-6 mb-2 ${pricingForm.pricing_type === 'tanto_alzado' ? 'text-[#e4002b]' : 'text-white/50'}`} />
                            <p className="text-white font-medium">Tanto Alzado</p>
                            <p className="text-white/50 text-xs">Flat Fee (+ IVA)</p>
                          </button>
                        </div>
                  </div>

                  {/* Porcentaje de Taquilla Options */}
                  {pricingForm.pricing_type === 'porcentaje_taquilla' && (
                    <div className="bg-[#141414] rounded-xl p-5">
                      <h3 className="text-white font-semibold mb-4">Porcentaje para el Exhibidor</h3>
                      <div className="grid grid-cols-3 gap-3">
                        {[45, 50, 55].map(pct => (
                          <button
                            key={pct}
                            onClick={() => setPricingForm(prev => ({ ...prev, exhibitor_percentage: pct }))}
                            className={`p-4 rounded-lg border text-center transition-all ${
                              pricingForm.exhibitor_percentage === pct 
                                ? 'border-blue-500 bg-blue-500/20 text-white' 
                                : 'border-white/10 text-white/60 hover:border-white/30'
                            }`}
                          >
                            <p className="text-2xl font-bold">{pct}%</p>
                            <p className="text-xs mt-1">Exhibidor</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-white/50 text-sm mt-4 text-center">
                        Distribuidora: {100 - pricingForm.exhibitor_percentage}%
                      </p>
                    </div>
                  )}

                  {/* Tanto Alzado Options */}
                  {pricingForm.pricing_type === 'tanto_alzado' && (
                    <div className="space-y-4">
                      {/* Cultural Content */}
                      <div className="bg-[#141414] rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-4">Ópera, Ballet, Concierto</h3>
                        <div className="space-y-4">
                          {/* Directo */}
                          <div>
                            <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Directo</label>
                            <div className="flex gap-2">
                              {[450, 350].map(price => (
                                <button
                                  key={price}
                                  onClick={() => setPricingForm(prev => ({ ...prev, directo_opera_ballet_concert: price }))}
                                  className={`px-4 py-2 rounded-lg border text-sm ${
                                    pricingForm.directo_opera_ballet_concert === price 
                                      ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                      : 'border-white/10 text-white/60'
                                  }`}
                                >
                                  {price}€
                                </button>
                              ))}
                              <input
                                type="number"
                                value={pricingForm.directo_opera_ballet_concert}
                                onChange={e => setPricingForm(prev => ({ ...prev, directo_opera_ballet_concert: parseInt(e.target.value) || 0 }))}
                                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                                placeholder="Otro..."
                              />
                            </div>
                          </div>
                          {/* Encore */}
                          <div>
                            <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Encore</label>
                            <div className="flex gap-2">
                              {[400, 300].map(price => (
                                <button
                                  key={price}
                                  onClick={() => setPricingForm(prev => ({ ...prev, encore_opera_ballet_concert: price }))}
                                  className={`px-4 py-2 rounded-lg border text-sm ${
                                    pricingForm.encore_opera_ballet_concert === price 
                                      ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                      : 'border-white/10 text-white/60'
                                  }`}
                                >
                                  {price}€
                                </button>
                              ))}
                              <input
                                type="number"
                                value={pricingForm.encore_opera_ballet_concert}
                                onChange={e => setPricingForm(prev => ({ ...prev, encore_opera_ballet_concert: parseInt(e.target.value) || 0 }))}
                                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                                placeholder="Otro..."
                              />
                            </div>
                          </div>
                          {/* Grabado */}
                          <div>
                            <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Grabado</label>
                            <div className="flex gap-2">
                              {[350, 250].map(price => (
                                <button
                                  key={price}
                                  onClick={() => setPricingForm(prev => ({ ...prev, grabado_opera_ballet_concert: price }))}
                                  className={`px-4 py-2 rounded-lg border text-sm ${
                                    pricingForm.grabado_opera_ballet_concert === price 
                                      ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                      : 'border-white/10 text-white/60'
                                  }`}
                                >
                                  {price}€
                                </button>
                              ))}
                              <input
                                type="number"
                                value={pricingForm.grabado_opera_ballet_concert}
                                onChange={e => setPricingForm(prev => ({ ...prev, grabado_opera_ballet_concert: parseInt(e.target.value) || 0 }))}
                                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                                placeholder="Otro..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Movie & Documentary */}
                      <div className="bg-[#141414] rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-4">Película, Documental</h3>
                        <div className="space-y-4">
                          {/* Película */}
                          <div>
                            <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Película</label>
                            <div className="flex gap-2">
                              {[350, 250].map(price => (
                                <button
                                  key={price}
                                  onClick={() => setPricingForm(prev => ({ ...prev, pelicula: price }))}
                                  className={`px-4 py-2 rounded-lg border text-sm ${
                                    pricingForm.pelicula === price 
                                      ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                      : 'border-white/10 text-white/60'
                                  }`}
                                >
                                  {price}€
                                </button>
                              ))}
                              <input
                                type="number"
                                value={pricingForm.pelicula}
                                onChange={e => setPricingForm(prev => ({ ...prev, pelicula: parseInt(e.target.value) || 0 }))}
                                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                                placeholder="Otro..."
                              />
                            </div>
                          </div>
                          {/* Documental */}
                          <div>
                            <label className="text-white/60 text-xs uppercase tracking-wider mb-2 block">Documental</label>
                            <div className="flex gap-2">
                              {[200, 150].map(price => (
                                <button
                                  key={price}
                                  onClick={() => setPricingForm(prev => ({ ...prev, documental: price }))}
                                  className={`px-4 py-2 rounded-lg border text-sm ${
                                    pricingForm.documental === price 
                                      ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                      : 'border-white/10 text-white/60'
                                  }`}
                                >
                                  {price}€
                                </button>
                              ))}
                              <input
                                type="number"
                                value={pricingForm.documental}
                                onChange={e => setPricingForm(prev => ({ ...prev, documental: parseInt(e.target.value) || 0 }))}
                                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
                                placeholder="Otro..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                    </div>
                  )}

                  {/* Tab: Alquiler Bravo */}
                  {pricingModalTab === 'bravo' && (
                    <div className="space-y-6">
                      {/* Bravo Rental Mode Section */}
                      <div className="bg-[#141414] rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                          <Box className="w-5 h-5 text-[#e4002b]" />
                          Modalidad de Alquiler del Bravo
                        </h3>
                        <div className="space-y-4">
                          {/* Rental Mode Options */}
                          <div className="grid grid-cols-1 gap-3">
                            {/* Por Evento */}
                            <button
                              onClick={() => setPricingForm(prev => ({ ...prev, bravo_rental_mode: 'por_evento' }))}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            pricingForm.bravo_rental_mode === 'por_evento' 
                              ? 'border-[#e4002b] bg-[#e4002b]/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">Por Evento</p>
                              <p className="text-white/50 text-sm">Pago por cada proyección</p>
                            </div>
                            <span className="text-[#e4002b] font-bold text-xl">50€</span>
                          </div>
                        </button>

                        {/* Anual Premium (2 años) */}
                        <button
                          onClick={() => setPricingForm(prev => ({ ...prev, bravo_rental_mode: 'anual_3_years' }))}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            pricingForm.bravo_rental_mode === 'anual_3_years' 
                              ? 'border-[#e4002b] bg-[#e4002b]/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">Anual Premium</p>
                              <p className="text-white/50 text-sm">Compromiso 2 años</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[#e4002b] font-bold">150€<span className="text-white/50 font-normal text-sm">/único</span></p>
                              <p className="text-white/60 text-sm">o 20€/mes</p>
                            </div>
                          </div>
                        </button>

                        {/* Anual Flexible (1 año) */}
                        <button
                          onClick={() => setPricingForm(prev => ({ ...prev, bravo_rental_mode: 'anual_1_year' }))}
                          className={`p-4 rounded-xl border text-left transition-all ${
                            pricingForm.bravo_rental_mode === 'anual_1_year' 
                              ? 'border-[#e4002b] bg-[#e4002b]/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white font-medium">Anual Flexible</p>
                              <p className="text-white/50 text-sm">Compromiso 1 año</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[#e4002b] font-bold">195€<span className="text-white/50 font-normal text-sm">/único</span></p>
                              <p className="text-white/60 text-sm">o 25€/mes</p>
                            </div>
                          </div>
                        </button>
                      </div>

                      {/* Payment Type (only for annual plans) */}
                      {(pricingForm.bravo_rental_mode === 'anual_3_years' || pricingForm.bravo_rental_mode === 'anual_1_year') && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <label className="text-white/60 text-sm mb-3 block">Modo de Pago</label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setPricingForm(prev => ({ ...prev, bravo_rental_payment: 'unico' }))}
                              className={`p-3 rounded-lg border text-center transition-all ${
                                pricingForm.bravo_rental_payment === 'unico' 
                                  ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                  : 'border-white/10 text-white/60 hover:border-white/30'
                              }`}
                            >
                              <p className="font-medium">Pago Único</p>
                              <p className="text-[#e4002b] font-bold mt-1">
                                {pricingForm.bravo_rental_mode === 'anual_3_years' ? '150€' : '195€'}
                              </p>
                            </button>
                            <button
                              onClick={() => setPricingForm(prev => ({ ...prev, bravo_rental_payment: 'mensual' }))}
                              className={`p-3 rounded-lg border text-center transition-all ${
                                pricingForm.bravo_rental_payment === 'mensual' 
                                  ? 'border-[#e4002b] bg-[#e4002b]/20 text-white' 
                                  : 'border-white/10 text-white/60 hover:border-white/30'
                              }`}
                            >
                              <p className="font-medium">Pago Mensual</p>
                              <p className="text-[#e4002b] font-bold mt-1">
                                {pricingForm.bravo_rental_mode === 'anual_3_years' ? '20€/mes' : '25€/mes'}
                              </p>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                    </div>
                  )}
                </div>

                {/* Save Button - Fixed at bottom */}
                <div className="p-6 border-t border-white/10 bg-[#1a1a1a] flex-shrink-0">
                  <button
                    onClick={() => saveCinemaPricing(selectedCinema.id)}
                    disabled={savingPricing}
                    className="w-full py-3 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] disabled:opacity-50"
                  >
                    {savingPricing ? 'Guardando...' : 'Guardar condiciones'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ANALYTICS - Dashboard de Analíticas */}
          {activeSection === 'analytics' && (
            <div className="space-y-6">
              {/* Header with period selector */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <BarChart3 className="w-7 h-7 text-[#e4002b]" />
                    Analíticas del Sitio Web
                  </h2>
                  <p className="text-white/60 mt-1">Estadísticas de visitas y comportamiento de usuarios en cinemastreaming.es</p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={analyticsPeriod}
                    onChange={(e) => {
                      setAnalyticsPeriod(Number(e.target.value));
                      loadAnalytics(Number(e.target.value));
                    }}
                    className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-[#e4002b]"
                  >
                    <option value={7}>Últimos 7 días</option>
                    <option value={30}>Últimos 30 días</option>
                    <option value={90}>Últimos 90 días</option>
                    <option value={365}>Último año</option>
                  </select>
                  <button
                    onClick={() => loadAnalytics(analyticsPeriod)}
                    disabled={loadingAnalytics}
                    className="px-4 py-2 bg-[#e4002b] text-white rounded-lg text-sm hover:bg-[#c70024] transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {loadingAnalytics ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                    Actualizar
                  </button>
                </div>
              </div>

              {/* Realtime Stats Banner */}
              {realtimeData && (
                <div className="bg-gradient-to-r from-green-500/20 to-[#1a1a1a] rounded-xl p-6 border border-green-500/30">
                  <div className="flex items-center gap-8">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-green-400 font-semibold">EN VIVO</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-white/60" />
                      <span className="text-white text-2xl font-bold">{realtimeData.active_users}</span>
                      <span className="text-white/60 text-sm">usuarios activos ahora</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-white/60" />
                      <span className="text-white text-lg font-semibold">{realtimeData.views_last_30_min}</span>
                      <span className="text-white/60 text-sm">vistas en últimos 30 min</span>
                    </div>
                  </div>
                  {realtimeData.recent_pages?.length > 0 && (
                    <div className="mt-4 flex items-center gap-4">
                      <span className="text-white/50 text-sm">Páginas activas:</span>
                      <div className="flex flex-wrap gap-2">
                        {realtimeData.recent_pages.map((p, i) => (
                          <span key={i} className="px-2 py-1 bg-white/10 rounded text-white/80 text-xs">
                            {p.page} ({p.views})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {loadingAnalytics && !analyticsData ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#e4002b]" />
                </div>
              ) : analyticsData ? (
                <>
                  {/* Main Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <Eye className="w-8 h-8 text-blue-400" />
                        <span className="text-blue-400 text-xs bg-blue-400/20 px-2 py-1 rounded-full">{analyticsPeriod} días</span>
                      </div>
                      <p className="text-white text-3xl font-bold">{analyticsData.total_views?.toLocaleString() || 0}</p>
                      <p className="text-white/50 text-sm mt-1">Visitas totales</p>
                    </div>
                    
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <Users className="w-8 h-8 text-green-400" />
                      </div>
                      <p className="text-white text-3xl font-bold">{analyticsData.unique_sessions?.toLocaleString() || 0}</p>
                      <p className="text-white/50 text-sm mt-1">Sesiones únicas</p>
                    </div>
                    
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <MousePointer className="w-8 h-8 text-purple-400" />
                      </div>
                      <p className="text-white text-3xl font-bold">{analyticsData.avg_pages_per_session || 0}</p>
                      <p className="text-white/50 text-sm mt-1">Páginas por sesión</p>
                    </div>
                    
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <div className="flex items-center justify-between mb-4">
                        <TrendingUp className="w-8 h-8 text-[#e4002b]" />
                      </div>
                      <p className="text-white text-3xl font-bold">
                        {analyticsData.daily_views?.length > 0 
                          ? Math.round(analyticsData.total_views / analyticsData.daily_views.length)
                          : 0}
                      </p>
                      <p className="text-white/50 text-sm mt-1">Promedio diario</p>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Daily Views Chart */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-[#e4002b]" />
                        Visitas por día
                      </h3>
                      {analyticsData.daily_views?.length > 0 ? (
                        <div className="space-y-2">
                          {/* Simple bar chart */}
                          <div className="flex items-end gap-1 h-32">
                            {analyticsData.daily_views.slice(-14).map((day, i) => {
                              const maxViews = Math.max(...analyticsData.daily_views.map(d => d.views));
                              const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                              return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                  <div 
                                    className="w-full bg-[#e4002b] rounded-t transition-all hover:bg-[#ff1a3d]"
                                    style={{ height: `${height}%`, minHeight: day.views > 0 ? '4px' : '0' }}
                                    title={`${day.date}: ${day.views} visitas`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-1">
                            {analyticsData.daily_views.slice(-14).map((day, i) => (
                              <div key={i} className="flex-1 text-center text-white/40 text-[10px] truncate">
                                {day.date?.slice(5)}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>

                    {/* Devices Distribution */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Smartphone className="w-5 h-5 text-[#e4002b]" />
                        Dispositivos
                      </h3>
                      {analyticsData.devices?.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.devices.map((device, i) => {
                            const total = analyticsData.devices.reduce((sum, d) => sum + d.count, 0);
                            const percentage = total > 0 ? Math.round((device.count / total) * 100) : 0;
                            const Icon = device.device === 'mobile' ? Smartphone : device.device === 'tablet' ? Tablet : Monitor;
                            const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500'];
                            return (
                              <div key={i} className="flex items-center gap-3">
                                <Icon className="w-5 h-5 text-white/60" />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-white capitalize">{device.device || 'Desconocido'}</span>
                                    <span className="text-white/60 text-sm">{percentage}%</span>
                                  </div>
                                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${colors[i % colors.length]} rounded-full transition-all`}
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-white/50 text-sm w-16 text-right">{device.count.toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>
                  </div>

                  {/* Top Pages & Content */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Pages */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#e4002b]" />
                        Páginas más visitadas
                      </h3>
                      {analyticsData.top_pages?.length > 0 ? (
                        <div className="space-y-2">
                          {analyticsData.top_pages.map((page, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                              <div className="flex items-center gap-3">
                                <span className="text-white/40 text-sm w-6">{i + 1}.</span>
                                <span className="text-white truncate max-w-[200px]" title={page.page}>
                                  {page.page === '/' ? 'Inicio' : page.page}
                                </span>
                              </div>
                              <span className="text-[#e4002b] font-semibold">{page.views.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>

                    {/* Top Content */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Film className="w-5 h-5 text-[#e4002b]" />
                        Contenidos más vistos
                      </h3>
                      {analyticsData.top_content?.length > 0 ? (
                        <div className="space-y-2">
                          {analyticsData.top_content.map((content, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                              <div className="flex items-center gap-3">
                                <span className="text-white/40 text-sm w-6">{i + 1}.</span>
                                <span className="text-white truncate max-w-[200px]" title={content.title}>
                                  {content.title}
                                </span>
                              </div>
                              <span className="text-[#e4002b] font-semibold">{content.views.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">No hay datos de contenidos aún</p>
                      )}
                    </div>
                  </div>

                  {/* Referrers & Languages */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Top Referrers */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Link2 className="w-5 h-5 text-[#e4002b]" />
                        Fuentes de tráfico
                      </h3>
                      {analyticsData.top_referrers?.length > 0 ? (
                        <div className="space-y-2">
                          {analyticsData.top_referrers.map((ref, i) => {
                            let domain = 'Directo';
                            try {
                              if (ref.referrer && ref.referrer !== '') {
                                domain = new URL(ref.referrer).hostname;
                              }
                            } catch (e) {
                              domain = ref.referrer?.substring(0, 30) || 'Desconocido';
                            }
                            return (
                              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                                <span className="text-white truncate max-w-[250px]" title={ref.referrer}>
                                  {domain}
                                </span>
                                <span className="text-green-400 font-semibold">{ref.count.toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">La mayoría del tráfico es directo</p>
                      )}
                    </div>

                    {/* Languages */}
                    <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#e4002b]" />
                        Idiomas de los visitantes
                      </h3>
                      {analyticsData.languages?.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.languages.map((lang, i) => {
                            const total = analyticsData.languages.reduce((sum, l) => sum + l.count, 0);
                            const percentage = total > 0 ? Math.round((lang.count / total) * 100) : 0;
                            const langNames = {
                              'es': 'Español',
                              'en': 'Inglés',
                              'ca': 'Catalán',
                              'fr': 'Francés',
                              'de': 'Alemán',
                              'pt': 'Portugués',
                              'it': 'Italiano'
                            };
                            return (
                              <div key={i} className="flex items-center gap-3">
                                <span className="text-2xl">{lang.language === 'es' ? '🇪🇸' : lang.language === 'en' ? '🇬🇧' : lang.language === 'ca' ? '🏴' : '🌐'}</span>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-white">{langNames[lang.language] || lang.language?.toUpperCase()}</span>
                                    <span className="text-white/60 text-sm">{percentage}%</span>
                                  </div>
                                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div 
                                      className="h-full bg-[#e4002b] rounded-full"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-white/50 text-center py-8">No hay datos de idiomas</p>
                      )}
                    </div>
                  </div>

                  {/* Info Banner */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <HelpCircle className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold mb-2">Sobre estas analíticas</h4>
                        <p className="text-white/70 text-sm leading-relaxed">
                          Estos datos se recopilan de forma anónima respetando la privacidad de los usuarios. 
                          Solo se registran visitas de usuarios que han aceptado las cookies de análisis. 
                          Para estadísticas más detalladas, considera integrar Google Analytics 4 con tu ID de seguimiento.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-[#141414] rounded-xl p-12 text-center">
                  <BarChart3 className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <h3 className="text-white text-lg font-semibold mb-2">Sin datos analíticos</h3>
                  <p className="text-white/60 mb-4">Aún no hay suficientes datos para mostrar estadísticas.</p>
                  <p className="text-white/40 text-sm">Los datos comenzarán a aparecer cuando los usuarios visiten el sitio y acepten las cookies de análisis.</p>
                </div>
              )}
            </div>
          )}

          {/* MASTER - Manual de Usuario */}
          {activeSection === 'master' && (
            <div className="space-y-8">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#e4002b]/20 to-[#1a1a1a] rounded-xl p-8 border border-[#e4002b]/30">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-[#e4002b] rounded-xl flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white text-2xl font-bold">Manual de Usuario - Bravo Cinemastreaming</h2>
                    <p className="text-white/60">Guía completa de todas las funciones del panel de administración</p>
                  </div>
                </div>
              </div>

              {/* Índice */}
              <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#e4002b]" />
                  Índice de contenidos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    { id: 'sec-dashboard', label: 'Dashboard', icon: LayoutDashboard },
                    { id: 'sec-hero', label: 'Hero / Slider', icon: Image },
                    { id: 'sec-sections', label: 'Secciones Home', icon: Layers },
                    { id: 'sec-testimonials', label: 'Reseñas', icon: Star },
                    { id: 'sec-content', label: 'Contenidos', icon: Film },
                    { id: 'sec-cinemas', label: 'Cines', icon: Building2 },
                    { id: 'sec-bookings', label: 'Reservas', icon: Calendar },
                    { id: 'sec-inquiries', label: 'Consultas', icon: AlertCircle },
                    { id: 'sec-conditions', label: 'Condiciones', icon: DollarSign }
                  ].map(item => (
                    <a 
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <item.icon className="w-4 h-4 text-[#e4002b]" />
                      <span className="text-white/80 text-sm">{item.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Section 1: Dashboard */}
              <div id="sec-dashboard" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <LayoutDashboard className="w-5 h-5 text-[#e4002b]" />
                  1. Dashboard
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>El Dashboard es la pantalla principal del panel de administración. Aquí encontrarás:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Estadísticas generales:</strong> Número total de contenidos, cines registrados, cines pendientes de aprobación y reservas totales.</li>
                    <li><strong className="text-white">Cines pendientes:</strong> Lista de cines que han solicitado registro y esperan aprobación. Puedes aprobarlos (✓) o rechazarlos (✗) directamente.</li>
                    <li><strong className="text-white">Reservas pendientes:</strong> Lista de solicitudes de proyección pendientes de confirmación.</li>
                  </ul>
                  <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-400 text-sm"><strong>Consejo:</strong> Revisa el Dashboard diariamente para no perder solicitudes de cines o reservas importantes.</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Hero / Slider */}
              <div id="sec-hero" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#e4002b]" />
                  2. Hero / Slider
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>El Hero Slider es el carrusel principal que aparece en la parte superior de la página de inicio.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Cómo añadir un nuevo slide:</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Haz clic en <span className="text-[#e4002b]">"+ Añadir Slide"</span></li>
                    <li>Selecciona un contenido del catálogo</li>
                    <li>Personaliza el título, subtítulo y descripción</li>
                    <li>Añade características (features) con iconos</li>
                    <li>Configura los botones de acción (CONTRATAR, VER FICHA)</li>
                    <li>Guarda los cambios</li>
                  </ol>
                  
                  <h4 className="text-white font-semibold mt-4">Gestión de slides:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Reordenar:</strong> Arrastra las tarjetas para cambiar el orden de aparición</li>
                    <li><strong className="text-white">Editar:</strong> Haz clic en el icono de lápiz para modificar</li>
                    <li><strong className="text-white">Eliminar:</strong> Haz clic en el icono de papelera</li>
                    <li><strong className="text-white">Activar/Desactivar:</strong> Usa el toggle para mostrar u ocultar slides</li>
                  </ul>
                </div>
              </div>

              {/* Section 3: Secciones Home */}
              <div id="sec-sections" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#e4002b]" />
                  3. Secciones Home
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Las secciones son los bloques de contenido que aparecen en la página principal debajo del Hero.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Tipos de secciones:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Carrusel de contenidos:</strong> Muestra una fila horizontal de contenidos</li>
                    <li><strong className="text-white">Carrusel detallado:</strong> Carrusel con más información visible</li>
                    <li><strong className="text-white">Carrusel de conciertos:</strong> Diseñado para eventos musicales</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Cómo crear una sección:</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Haz clic en <span className="text-[#e4002b]">"Nueva sección"</span></li>
                    <li>Introduce el nombre de la sección (ej: "Estrenos del mes")</li>
                    <li>Selecciona el tipo de carrusel</li>
                    <li>Guarda la sección</li>
                    <li>Arrastra contenidos a la sección desde el panel lateral</li>
                  </ol>
                </div>
              </div>

              {/* Section 4: Reseñas */}
              <div id="sec-testimonials" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#e4002b]" />
                  4. Reseñas (Testimonios)
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Las reseñas son testimonios de cines satisfechos que aparecen en la página de inicio.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Cómo añadir una reseña:</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Haz clic en <span className="text-[#e4002b]">"+ Nueva Reseña"</span></li>
                    <li>Introduce el nombre del cine o responsable</li>
                    <li>Escribe el cargo o rol</li>
                    <li>Redacta el testimonio (máx. 200 caracteres recomendado)</li>
                    <li>Añade la URL de una imagen (opcional)</li>
                    <li>Selecciona la valoración en estrellas</li>
                    <li>Guarda la reseña</li>
                  </ol>
                  
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm"><strong>Importante:</strong> Las reseñas aparecen en un carrusel automático en la página principal. Asegúrate de que sean testimonios reales y verificables.</p>
                  </div>
                </div>
              </div>

              {/* Section 5: Contenidos */}
              <div id="sec-content" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Film className="w-5 h-5 text-[#e4002b]" />
                  5. Contenidos
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Esta sección permite gestionar todo el catálogo de óperas, ballets, conciertos y documentales.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Crear nuevo contenido:</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Haz clic en <span className="text-[#e4002b]">"+ Nuevo Contenido"</span></li>
                    <li><strong>Información básica:</strong> Título, compositor/director, teatro, categoría, temporada</li>
                    <li><strong>Imágenes:</strong> URL de imagen principal y hero image</li>
                    <li><strong>Estado:</strong> Disponible, Próximamente (con fecha de estreno)</li>
                    <li><strong>Tipo especial:</strong>
                      <ul className="list-disc list-inside ml-4 mt-2">
                        <li><strong className="text-[#e4002b]">DIRECTO:</strong> Para eventos en vivo - incluye día y hora de emisión</li>
                        <li><strong className="text-amber-400">ENCORE:</strong> Para reprogramaciones - incluye rango de fechas</li>
                      </ul>
                    </li>
                    <li><strong>Sinopsis:</strong> Descripción corta y extendida</li>
                    <li><strong>Reparto:</strong> Lista de artistas y personajes</li>
                    <li><strong>Info producción:</strong> Teatro, año, duración, subtítulos</li>
                    <li><strong>Materiales:</strong> URLs de pósters, tráilers, press kits para cines</li>
                  </ol>
                  
                  <h4 className="text-white font-semibold mt-4">Notificar a cines:</h4>
                  <p>Al crear o editar contenido, puedes enviar una notificación por email a todos los cines aprobados:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Al guardar:</strong> Marca la casilla "Al guardar" para notificar automáticamente</li>
                    <li><strong className="text-white">Enviar ahora:</strong> Haz clic en el botón para enviar inmediatamente</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Acciones disponibles:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Editar:</strong> Modificar cualquier campo del contenido</li>
                    <li><strong className="text-white">Duplicar:</strong> Crear una copia del contenido</li>
                    <li><strong className="text-white">Eliminar:</strong> Solo si no tiene reservas activas</li>
                  </ul>
                </div>
              </div>

              {/* Section 6: Cines */}
              <div id="sec-cinemas" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#e4002b]" />
                  6. Cines
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Gestión completa de los cines registrados en la plataforma.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Estados de un cine:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-yellow-400">Pendiente:</strong> Solicitud de registro recibida, esperando aprobación</li>
                    <li><strong className="text-green-400">Aprobado:</strong> Cine activo con acceso completo a la plataforma</li>
                    <li><strong className="text-gray-400">Inactivo:</strong> Cine desactivado temporalmente</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Acciones disponibles:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Ver ficha:</strong> Información completa del cine (datos de contacto, especificaciones técnicas)</li>
                    <li><strong className="text-white">Aprobar/Rechazar:</strong> Para cines pendientes</li>
                    <li><strong className="text-white">Inactivar:</strong> Desactivar temporalmente sin eliminar</li>
                    <li><strong className="text-white">Eliminar:</strong> Solo si no tiene reservas activas</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Columna "Bravo":</h4>
                  <p>Muestra el número de dispositivo Bravo asignado a cada cine (si aplica).</p>
                  
                  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 text-sm"><strong>Flujo de registro:</strong> Cine se registra → Recibe email de confirmación → Admin aprueba → Cine recibe email de bienvenida con credenciales → Cine puede acceder al portal</p>
                  </div>
                </div>
              </div>

              {/* Section 7: Reservas */}
              <div id="sec-bookings" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#e4002b]" />
                  7. Reservas
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Control de todas las solicitudes de proyección de los cines.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Estados de una reserva:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-yellow-400">Pendiente:</strong> Solicitud recibida, esperando confirmación</li>
                    <li><strong className="text-green-400">Aprobada:</strong> Reserva confirmada</li>
                    <li><strong className="text-red-400">Rechazada:</strong> Solicitud denegada</li>
                    <li><strong className="text-blue-400">Completada:</strong> Proyección realizada</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Filtros disponibles:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Por mes:</strong> Filtra reservas de un mes específico</li>
                    <li><strong className="text-white">Por cine:</strong> Ver reservas de un cine concreto</li>
                    <li><strong className="text-white">Por contenido:</strong> Ver qué cines han reservado un contenido</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Información de cada reserva:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Contenido solicitado</li>
                    <li>Cine que solicita</li>
                    <li>Fecha y hora de proyección</li>
                    <li>Si necesita soporte de marketing</li>
                    <li>Fecha de solicitud</li>
                  </ul>
                </div>
              </div>

              {/* Section 8: Consultas */}
              <div id="sec-inquiries" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[#e4002b]" />
                  8. Consultas
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Gestión de solicitudes de información recibidas a través del formulario de contacto.</p>
                  
                  <h4 className="text-white font-semibold mt-4">Información de cada consulta:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Nombre del solicitante</li>
                    <li>Email de contacto</li>
                    <li>Mensaje/consulta</li>
                    <li>Fecha de recepción</li>
                    <li>Estado (pendiente/respondida)</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Acciones:</h4>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Marcar como respondida:</strong> Cuando hayas contestado al email</li>
                    <li><strong className="text-white">Eliminar:</strong> Para consultas spam o duplicadas</li>
                  </ul>
                </div>
              </div>

              {/* Section 9: Condiciones */}
              <div id="sec-conditions" className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#e4002b]" />
                  9. Condiciones Económicas
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Configuración de las condiciones económicas especiales para cada cine.</p>
                  
                  <h4 className="text-white font-semibold mt-4">¿Cómo funciona?</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Selecciona un cine de la lista</li>
                    <li>Verás su ficha completa con datos de contacto y especificaciones técnicas</li>
                    <li>En la sección "Condiciones Económicas" puedes configurar:
                      <ul className="list-disc list-inside ml-4 mt-2">
                        <li>MG (Mínimo Garantizado)</li>
                        <li>Porcentaje de reparto sobre taquilla</li>
                        <li>Notas adicionales</li>
                      </ul>
                    </li>
                    <li>Guarda los cambios</li>
                  </ol>
                  
                  <h4 className="text-white font-semibold mt-4">Contenidos con condiciones especiales:</h4>
                  <p>Si un contenido está marcado con "Condiciones económicas especiales", el cine deberá consultar las condiciones específicas antes de contratar.</p>
                  
                  <div className="mt-4 p-4 bg-[#e4002b]/10 border border-[#e4002b]/30 rounded-lg">
                    <p className="text-[#e4002b] text-sm"><strong>Nota:</strong> Las condiciones económicas son privadas y solo visibles para el administrador. Los cines ven un mensaje genérico indicando que deben contactar para conocer las condiciones.</p>
                  </div>
                </div>
              </div>

              {/* Portal del Cine */}
              <div className="bg-gradient-to-r from-blue-500/20 to-[#1a1a1a] rounded-xl p-6 border border-blue-500/30">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-blue-400" />
                  Portal del Cine (Área de usuarios)
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>Los cines aprobados tienen acceso a su propio portal donde pueden:</p>
                  
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong className="text-white">Nueva solicitud:</strong> Solicitar proyecciones de contenidos del catálogo</li>
                    <li><strong className="text-white">Mis solicitudes:</strong> Ver el estado de sus reservas (pendientes, aprobadas, rechazadas)</li>
                    <li><strong className="text-white">Histórico:</strong> Consultar proyecciones pasadas</li>
                    <li><strong className="text-white">Calendario:</strong> Ver sus proyecciones programadas en formato calendario</li>
                    <li><strong className="text-white">Cuenta:</strong> Gestionar datos de perfil</li>
                  </ul>
                  
                  <h4 className="text-white font-semibold mt-4">Flujo de contratación:</h4>
                  <ol className="list-decimal list-inside space-y-2 ml-4">
                    <li>Cine selecciona contenido del catálogo</li>
                    <li>Elige fecha y hora de proyección (respetando restricciones de estreno)</li>
                    <li>Indica si necesita soporte de marketing</li>
                    <li>Envía solicitud</li>
                    <li>Admin aprueba o rechaza</li>
                    <li>Cine recibe notificación por email</li>
                  </ol>
                </div>
              </div>

              {/* Emails automáticos */}
              <div className="bg-[#141414] rounded-xl p-6 border border-white/10">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#e4002b]" />
                  Emails Automáticos
                </h3>
                <div className="space-y-4 text-white/70 text-sm leading-relaxed">
                  <p>La plataforma envía emails automáticos en los siguientes casos:</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="p-4 bg-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Para Cines:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Confirmación de registro</li>
                        <li>Aprobación de cuenta</li>
                        <li>Rechazo de cuenta</li>
                        <li>Reserva aprobada</li>
                        <li>Reserva rechazada</li>
                        <li>Nuevo contenido disponible</li>
                        <li>Oferta de condiciones especiales</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-white/5 rounded-lg">
                      <h4 className="text-white font-semibold mb-2">Para Admin:</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Nuevo cine registrado</li>
                        <li>Nueva solicitud de reserva</li>
                        <li>Nueva consulta recibida</li>
                        <li>Oferta aceptada por cine</li>
                        <li>Oferta rechazada por cine</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips y mejores prácticas */}
              <div className="bg-gradient-to-r from-green-500/20 to-[#1a1a1a] rounded-xl p-6 border border-green-500/30">
                <h3 className="text-white text-lg font-bold mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-green-400" />
                  Tips y Mejores Prácticas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-white/70 text-sm">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Revisa el Dashboard diariamente para atender solicitudes pendientes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Mantén actualizado el Hero Slider con los estrenos más relevantes</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Usa la función "Notificar a cines" para comunicar nuevos contenidos importantes</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Configura correctamente las fechas de DIRECTOS para evitar confusiones</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Añade todos los materiales de marketing para facilitar la promoción a los cines</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <span>Revisa las reseñas para mantener contenido actualizado y relevante</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Version info */}
              <div className="text-center text-white/40 text-xs py-4">
                <p>Manual de Usuario - Bravo Cinemastreaming Admin v1.0</p>
                <p>Última actualización: Febrero 2026</p>
              </div>
            </div>
          )}

          {/* Turandot Inscripciones Section */}
          {activeSection === 'turandot-inscripciones' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white text-2xl font-bold">Inscripciones Turandot Les Arts</h2>
                  <p className="text-white/50 text-sm mt-1">Solicitudes de municipios interesados en la retransmisión del centenario de Turandot</p>
                </div>
                <a 
                  href="/turandot-inscripciones" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#e4002b]/20 text-[#e4002b] rounded-lg text-sm hover:bg-[#e4002b]/30 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Ver página
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Pendientes</p>
                  <p className="text-yellow-400 text-2xl font-bold">{turandotInscripciones.filter(i => i.status === 'pending').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Contactados</p>
                  <p className="text-blue-400 text-2xl font-bold">{turandotInscripciones.filter(i => i.status === 'contacted').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Confirmados</p>
                  <p className="text-green-400 text-2xl font-bold">{turandotInscripciones.filter(i => i.status === 'confirmed').length}</p>
                </div>
                <div className="bg-[#141414] rounded-xl p-4">
                  <p className="text-white/50 text-sm mb-1">Total</p>
                  <p className="text-white text-2xl font-bold">{turandotInscripciones.length}</p>
                </div>
              </div>

              {/* Inscripciones Table */}
              <div className="bg-[#141414] rounded-xl overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <h3 className="text-white font-semibold">Municipios inscritos</h3>
                  <span className="text-white/50 text-sm">{turandotInscripciones.length} inscripciones</span>
                </div>
                
                {turandotInscripciones.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                    <p className="text-white/50">No hay inscripciones aún</p>
                    <p className="text-white/30 text-sm mt-2">Las inscripciones aparecerán aquí cuando los municipios completen el formulario</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Municipio</th>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Responsable</th>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Contacto</th>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Aforo</th>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Fecha</th>
                          <th className="text-left text-white/60 text-xs uppercase tracking-wider px-4 py-3">Estado</th>
                          <th className="text-right text-white/60 text-xs uppercase tracking-wider px-4 py-3">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {turandotInscripciones.map(inscripcion => (
                          <tr key={inscripcion.id} className="hover:bg-white/5">
                            <td className="px-4 py-4">
                              <p className="text-white font-medium">{inscripcion.municipio}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-white/70 text-sm">{inscripcion.nombre || '-'}</p>
                              <p className="text-white/50 text-xs">{inscripcion.cargo || '-'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-white/70 text-sm">{inscripcion.email}</p>
                              <p className="text-white/50 text-xs">{inscripcion.telefono}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-white/70">{inscripcion.aforo || '-'}</p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-white/50 text-sm">
                                {inscripcion.created_at ? new Date(inscripcion.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <select
                                value={inscripcion.status || 'pending'}
                                onChange={(e) => updateTurandotInscripcionStatus(inscripcion.id, e.target.value)}
                                className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                                  inscripcion.status === 'confirmed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                  inscripcion.status === 'contacted' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                  inscripcion.status === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                  'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                                }`}
                              >
                                <option value="pending" className="bg-[#1a1a1a] text-white">Pendiente</option>
                                <option value="contacted" className="bg-[#1a1a1a] text-white">Contactado</option>
                                <option value="confirmed" className="bg-[#1a1a1a] text-white">Confirmado</option>
                                <option value="cancelled" className="bg-[#1a1a1a] text-white">Cancelado</option>
                              </select>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <a 
                                href={`mailto:${inscripcion.email}?subject=Retransmisión%20Turandot%20-%20${encodeURIComponent(inscripcion.municipio)}`}
                                className="text-[#e4002b] hover:text-white text-sm"
                              >
                                Contactar
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RAG Documents Section */}
          {activeSection === 'rag-documents' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-white text-2xl font-bold">Base de Conocimiento IA</h2>
                  <p className="text-white/50 text-sm mt-1">Sube documentos PDF para entrenar el asistente de soporte</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-white/50 text-xs">Documentos: {ragStats.documents_count}</p>
                    <p className="text-white/50 text-xs">Fragmentos: {ragStats.total_chunks}</p>
                  </div>
                  <label className={`px-4 py-2 bg-[#e4002b] text-white rounded-lg cursor-pointer hover:bg-[#c4002b] flex items-center gap-2 ${uploadingDoc ? 'opacity-50 pointer-events-none' : ''}`}>
                    {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploadingDoc ? 'Procesando...' : 'Subir PDF'}
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleUploadRagDocument}
                      className="hidden"
                      disabled={uploadingDoc}
                    />
                  </label>
                </div>
              </div>

              {docUploadError && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">
                  {docUploadError}
                </div>
              )}

              {/* Info Card */}
              <div className="bg-gradient-to-r from-[#e4002b]/10 to-[#1a1a1a] rounded-xl p-6 border border-[#e4002b]/30">
                <div className="flex items-start gap-4">
                  <Bot className="w-8 h-8 text-[#e4002b] flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">¿Cómo funciona?</h3>
                    <ul className="text-white/60 text-sm space-y-1">
                      <li>• Sube documentos PDF con información sobre Bravo, manuales, FAQs, etc.</li>
                      <li>• El sistema extrae el texto y lo divide en fragmentos</li>
                      <li>• Cuando un usuario hace una pregunta, el asistente busca información relevante</li>
                      <li>• Las respuestas se generan combinando el conocimiento con los documentos subidos</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Documents List */}
              <div className="bg-[#141414] rounded-xl border border-white/10">
                <div className="px-6 py-4 border-b border-white/10">
                  <h3 className="text-white font-semibold">Documentos en la base de conocimiento</h3>
                </div>
                
                {ragDocuments.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="w-16 h-16 text-white/10 mx-auto mb-4" />
                    <p className="text-white/40 mb-2">No hay documentos</p>
                    <p className="text-white/30 text-sm">Sube tu primer PDF para empezar a entrenar el asistente</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/10">
                    {ragDocuments.map((doc) => (
                      <div key={doc.id} className="px-6 py-4 flex items-center justify-between hover:bg-white/5">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#e4002b]/20 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#e4002b]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{doc.name}</p>
                            <p className="text-white/40 text-xs">
                              {doc.chunks_count} fragmentos • Subido {new Date(doc.uploaded_at).toLocaleDateString('es-ES')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteRagDocument(doc.id, doc.name)}
                          className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Eliminar documento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Tips */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="text-amber-400 text-sm">
                  <strong>Recomendaciones:</strong> Sube documentos claros y bien estructurados. Los manuales de usuario, FAQs, y documentación técnica funcionan muy bien. Evita PDFs con muchas imágenes o tablas complejas.
                </p>
              </div>
            </div>
          )}

          {/* Chat Preview Section */}
          {activeSection === 'chat-preview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-white text-2xl font-bold">Preview del Chat de Soporte</h2>
                <p className="text-white/50 text-sm mt-1">Prueba el asistente antes de compartir el enlace</p>
              </div>

              <div className="bg-[#141414] rounded-xl border border-white/10 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#e4002b]/20 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-[#e4002b]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Chat de Soporte Bravo</p>
                      <p className="text-white/40 text-xs">Página: /chat-soporte</p>
                    </div>
                  </div>
                  <a
                    href="/chat-soporte"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#e4002b] text-white rounded-lg hover:bg-[#c4002b] flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Abrir chat
                  </a>
                </div>

                <div className="aspect-video bg-black/50 rounded-lg overflow-hidden">
                  <iframe
                    src="/chat-soporte"
                    className="w-full h-full border-0"
                    title="Chat Preview"
                  />
                </div>

                <div className="mt-4 p-4 bg-white/5 rounded-lg">
                  <p className="text-white/60 text-sm">
                    <strong className="text-white">URL del chat:</strong>{' '}
                    <code className="bg-black/50 px-2 py-1 rounded">{window.location.origin}/chat-soporte</code>
                  </p>
                  <p className="text-white/40 text-xs mt-2">
                    Esta página no aparece en la navegación principal. Comparte el enlace solo con quienes necesiten acceder.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
